import React, { useState, useEffect, useContext, createContext, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as workflowUtils from '../../../utils/workflowUtils';
import * as fieldCategoryUtils from '../../../utils/fieldCategoryUtils';
import WorkflowStepAssignDialog from './WorkflowStepAssignDialog';
import ServiceCostDisplay from '../../../Components/Invoice/ServiceCostDisplay';
import CalculateIcon from '@mui/icons-material/Calculate';
import Avatar from '@mui/material/Avatar';
import QuickEmployeeSearch from '../../../Components/EmployeeSearch/QuickEmployeeSearch';
import AuthenticatedImage from '../../../Components/AuthenticatedImage';
import { showLoader, dismissLoader, showSuccessMessage, showErroMessage } from '../../../LayoutContainers/AppLayout/redux/AppSlice';
import workflowService from '../../../services/workflow';
import AppService from '../../../services/api';
import { fetchOrganisationAddress } from '../../../utils/employeeContextUtils';
import { API_ENDPOINTS } from '../../../Api/endpoints';
import { DOCUMENT_TYPE_MAPPING } from '../../../Configurations/StatFieldData';
import { FEATURE_WORKFLOW_DOC_GROUPS } from '../../../Configurations/FeatureFlags';
import { getAuthToken } from '../../../utils/authUtils';
import { mapCustomerDataToWorkflowFields } from '../../../utils/customerWorkflowUtils';
import { mapEmployeeDataToWorkflowFields } from '../../../utils/employeeWorkflowUtils';
import pxToRem from "../../../Themes/Functions/pxToRem";
import { WorkflowSelectionPanel } from '../../../Components/WorkflowSelection';
// Debug overlay imports
import CloseIcon from '@mui/icons-material/Close';
import BugReportIcon from '@mui/icons-material/BugReport';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GetAppIcon from '@mui/icons-material/GetApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachmentIcon from '@mui/icons-material/Attachment';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
// InfoIcon is imported below as part of the named imports
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';

import {
  FieldFactory,
  DateField,
  EmailField,
  PhoneField,
  SelectField,
  TextField as CustomTextField,
  TextAreaField,
  NumberField,
  CategorizedFields
} from '../../../Components/WorkflowFields';

import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Switch,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as AddressIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { styled } from '@mui/material/styles';
import { 
  MdAttachFile, 
  MdUpload, 
  MdDelete, 
  MdFilePresent,
  MdDownload,
  MdPreview,
  MdPerson
} from 'react-icons/md';
import QuickEmployeeAddDialog from '../../../Components/QuickEmployeeAdd/QuickEmployeeAddDialog';

// Styled component for visually hidden file input
const VisuallyHiddenInput = styled('input')({  
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
  padding: 0,
  border: 0,
});

// Debug logger (disabled by default). Enable by setting localStorage.DEBUG_WORKFLOW = '1'
const debugLog = (...args) => {
  try {
    const enabled = typeof window !== 'undefined' && window.localStorage && localStorage.getItem('DEBUG_WORKFLOW') === '1';
    if (enabled) {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  } catch (e) { /* noop */ }
};

// Create a context for sharing state with the debug overlay
const NewWorkflowRequestContext = createContext({});

const NewWorkflowRequest = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [requestNumber, setRequestNumber] = useState(null);
  const [instanceId, setInstanceId] = useState(null);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [assignedWorkflows, setAssignedWorkflows] = useState([]);
  // Pre-read URL params for immediate state initialization
  const initParams = new URLSearchParams(window.location.search);
  const initWorkflowId = initParams.get('workflowId');
  const [selectedTargetType, setSelectedTargetType] = useState(initWorkflowId ? 'ORGANIZATION' : '');
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const [preSelectWorkflowId, setPreSelectWorkflowId] = useState(initWorkflowId || null);
  const [workflowDetails, setWorkflowDetails] = useState(null);
  const [canAssign, setCanAssign] = useState(false);
  
  const [formData, setFormData] = useState({
    requestTitle: '',
    description: '',
    priority: 'MEDIUM',
    additionalInfo: {},
    customerUid: ''
  });

  // Authenticated file download helper for grouped documents
  const handleGroupedFileDownload = async (filePath, fileName = 'document') => {
    if (!filePath) return;
    try {
      const url = API_ENDPOINTS.FILES.DOWNLOAD(filePath);
      const token = getAuthToken && getAuthToken();
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) {
        throw new Error(`Download failed: ${res.status} ${res.statusText}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (err) {
      console.error('Download failed for filePath', filePath, err);
      setGroupedDocsError('Failed to download file');
    }
  };
  
  // Debug state
  // Debug overlay state removed

  // Customer UID validation state
  const [customerUidValidation, setCustomerUidValidation] = useState({
    error: null,
    success: false,
    customerInfo: null,
    validating: false
  });
  
  // Employee assignment state is defined below
  
  // Customer creation dialog state
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    uid: '',
    firstNameEn: '',
    middleNameEn: '',
    lastNameEn: '',
    nameEn: '',
    mobileNumber: '',
    email: ''
  });
  
  // Track which steps are assigned
  const [selfAssignedSteps, setSelfAssignedSteps] = useState({});
  
  // Track assigned users for each step
  const [assignedUsers, setAssignedUsers] = useState({});
  
  // State for assignment dialog
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [currentStepForAssignment, setCurrentStepForAssignment] = useState(null);
  
  // State for cost breakdown dialog
  const [showCostDialog, setShowCostDialog] = useState(false);
  
  // Track validation errors for each field
  const [validationErrors, setValidationErrors] = useState({});
  
  // State to store workflow fields for display
  const [workflowFields, setWorkflowFields] = useState({
    customerInfoFields: [],
    formFields: [],
    allFields: []
  });
  
  // State to track if services are enabled
  const [servicesEnabled, setServicesEnabled] = useState(true); // Default to enabled
  
  // State for employee assignment
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Phase 2A: Grouped employee documents (read-only panel behind feature flag)
  const [groupedDocs, setGroupedDocs] = useState([]);
  const [groupedDocsLoading, setGroupedDocsLoading] = useState(false);
  const [groupedDocsError, setGroupedDocsError] = useState(null);
  // Phase 3A: Selections state (per logical document type)
  const [groupedSelections, setGroupedSelections] = useState({});

  // Helper to normalize doc type to a field-like key
  const mapDocTypeToKey = (docType) => {
    const dt = (docType || '').toLowerCase();
    if (dt.includes('passport')) return 'passportCopy';
    if (dt.includes('visa')) return 'visaPage';
    if (dt.includes('emirates') && dt.includes('id')) return 'emiratesId';
    if (dt.includes('photo') || dt.includes('picture')) return 'photo';
    if (dt.includes('birth') && dt.includes('cert')) return 'birthCertificate';
    if (dt.includes('labour') && dt.includes('contract')) return 'labourContract';
    if (dt.includes('salary') && dt.includes('cert')) return 'salaryCertificate';
    if (dt.includes('shinasnama')) return 'shinasnama';
    if (dt.includes('police')) return 'policeClearanceCertificate';
    if (dt.includes('resume')) return 'resume';
    if (dt.includes('education') && dt.includes('cert')) return 'educationalCertificate';
    if (dt.includes('experience') && dt.includes('cert')) return 'experienceCertificate';
    return (docType || 'Documents').replace(/\s+/g, '_').toLowerCase();
  };

  // Phase 3A: handlers for selecting a group and a specific document version
  const handleSelectGroup = (docKey, group) => {
    if (!group) return;
    const docs = Array.isArray(group?.documents) ? group.documents : (Array.isArray(group?.files) ? group.files : []);
    const primary = docs.find(d => d.isPrimary) || docs[0] || null;
    setGroupedSelections(prev => ({
      ...prev,
      [docKey]: {
        documentTypeKey: docKey,
        documentGroupId: group.documentGroupId,
        backendDocumentType: group?.common?.documentType || null,
        selectedDocumentId: primary?.id || null,
        fileName: primary?.fileName || null,
        filePath: primary?.filePath || primary?.path || primary?.documentUrl || primary?.s3Key || null
      }
    }));
  };

  const handleSelectDoc = (docKey, group, doc) => {
    if (!group || !doc) return;
    setGroupedSelections(prev => ({
      ...prev,
      [docKey]: {
        documentTypeKey: docKey,
        documentGroupId: group.documentGroupId,
        backendDocumentType: group?.common?.documentType || null,
        selectedDocumentId: doc.id,
        fileName: doc.fileName || null,
        filePath: doc.filePath || doc.path || doc.documentUrl || doc.s3Key || null
      }
    }));
  };
  
  // Document management state removed

  // File viewer dialog state
  const [fileViewerDialog, setFileViewerDialog] = useState({
    open: false,
    fileName: '',
    fileUrl: '',
    fileType: '',
    loading: false,
    error: null
  });

  // State for service costs
  const [serviceCosts, setServiceCosts] = useState({
    totalServiceCharge: 0,
    totalVat: 0,
    totalAmount: 0
  });

  const [openQuickAddEmployee, setOpenQuickAddEmployee] = useState(false);
  
  // Define document types
  const documentTypes = [
    { value: 'PASSPORT', label: 'Passport' },
    { value: 'VISA', label: 'Visa' },
    { value: 'ID_CARD', label: 'ID Card' },
    { value: 'EMPLOYMENT_CONTRACT', label: 'Employment Contract' },
    { value: 'AUTHORIZATION_LETTER', label: 'Authorization Letter' },
    { value: 'OTHER', label: 'Other' }
  ];
  
  // Maximum file size in bytes (5MB)
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  
  // Allowed file types
  const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  
  // State for step assignment types (self vs organization)
  const [stepAssignmentTypes, setStepAssignmentTypes] = useState({});
  
  // Get organization name from localStorage
  const organizationName = localStorage.getItem('organization_name') || 'Your Organization';
  
  // Validation functions for different field types
  const validateField = (fieldName, value, fieldType) => {
    let isValid = true;
    let errorMessage = '';

    // Check if fieldName and fieldType are defined
    if (!fieldName || !fieldType) return true;
    
    switch(fieldType.toString().toUpperCase()) {
      case 'EMAIL':
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        isValid = emailRegex.test(value);
        errorMessage = isValid ? '' : 'Please enter a valid email address';
        break;
        
      case 'PHONE':
        // Allow various phone formats with optional country codes
        const phoneRegex = /^\+?[0-9\s\-\(\)]{8,20}$/;
        isValid = phoneRegex.test(value);
        errorMessage = isValid ? '' : 'Please enter a valid phone number';
        break;
        
      case 'DATE':
        // Check if it's a valid date
        const dateObj = new Date(value);
        isValid = !isNaN(dateObj.getTime());
        errorMessage = isValid ? '' : 'Please enter a valid date';
        break;
        
      case 'NUMBER':
        isValid = !isNaN(Number(value));
        errorMessage = isValid ? '' : 'Please enter a valid number';
        break;
        
      case 'ADDRESS':
        // Simple validation - just check if it's not empty and has some minimum length
        isValid = value && value.trim().length >= 5;
        errorMessage = isValid ? '' : 'Please enter a valid address (minimum 5 characters)';
        break;
        
      case 'FILE':
      case 'PDF':
      case 'IMAGE':
        // For file uploads, check if we have a file object
        isValid = value && (typeof value === 'object');
        errorMessage = isValid ? '' : 'Please upload a valid file';
        break;

      default:
        // For text fields, just check if required and not empty
        if (value === undefined || value === null) {
          isValid = false;
          errorMessage = 'This field is required';
        } else if (typeof value === 'string' && value.trim() === '') {
          isValid = false;
          errorMessage = 'This field is required';
        }
        break;
    }
    
    // Update validation errors state
    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: errorMessage
    }));
    
    return isValid;
  };
  
  // Function to validate all fields before submission
  const validateAllFields = () => {
    let isValid = true;
    const newErrors = {};
    
    // Validate required form fields
    if (!formData.requestTitle.trim()) {
      newErrors.requestTitle = 'Request title is required';
      isValid = false;
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
      isValid = false;
    }
    
    // Resolve a field's storage key the SAME way the render does (customerInfoFields render
    // and CategorizedFields), so validation reads the value the user actually entered. Some
    // dynamic fields have a null/undefined name or code, so a single-property lookup (field.name
    // / field.code) misses them and wrongly reports "required".
    const resolveFieldKey = (field, index) => {
      const code = field.code || '';
      const isDynamicField = code.startsWith('field_');
      // Coded dynamic fields are keyed by their title (like null-code fields), so tickets show the
      // readable title rather than the field_xxxx code. Fall back to the code if no title exists.
      return isDynamicField ? (field.label || field.title || code) : (field.name || field.code || field.label || `field_${index}`);
    };

    // Validate customer info fields
    if (workflowFields.customerInfoFields) {
      workflowFields.customerInfoFields.forEach((field, index) => {
        if (field.required) {
          const key = resolveFieldKey(field, index);
          const label = field.label || field.name || key;
          const value = formData.additionalInfo[key];
          if (!value) {
            newErrors[key] = `${label} is required`;
            isValid = false;
          } else {
            // Validate based on field type
            const fieldIsValid = validateField(key, value, field.fieldType);
            if (!fieldIsValid) {
              newErrors[key] = `${label} is invalid`;
              isValid = false;
            }
          }
        }
      });
    }

    // Validate form fields
    if (workflowFields.formFields) {
      workflowFields.formFields.forEach((field, index) => {
        if (field.required) {
          const key = resolveFieldKey(field, index);
          const label = field.label || field.name || key;
          const value = formData.additionalInfo[key];
          if (!value) {
            newErrors[key] = `${label} is required`;
            isValid = false;
          } else {
            // Validate based on field type
            const fieldIsValid = validateField(key, value, field.fieldType);
            if (!fieldIsValid) {
              newErrors[key] = `${label} is invalid`;
              isValid = false;
            }
          }
        }
      });
    }
    setValidationErrors(newErrors);
    return { isValid, errors: newErrors };
  };

  // Get organization ID from URL params if available
  const [organizationId, setOrganizationId] = useState(null);
  
  // Function to get the current user's organization ID
  const getCurrentUserOrganizationId = async () => {
    try {
      // Try to get the current user's info from the auth endpoint
      const response = await AppService.get('/api/auth/me');
      debugLog('Current user info:', response);
      
      if (response && response.organisationId) {
        return response.organisationId;
      }
      
      // If we couldn't get it from the auth endpoint, try to get it from the JWT token
      const token = localStorage.getItem('jwt_token');
      if (token && token.startsWith('ey')) {
        try {
          // Extract the JWT payload
          const parts = token.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            debugLog('JWT payload:', payload);
            
            if (payload.org) {
              return payload.org;
            }
          }
        } catch (e) {
          console.error('Error parsing JWT token:', e);
        }
      }
      
      // If we still couldn't get it, return the first customer organization ID from assignedWorkflows
      if (assignedWorkflows && assignedWorkflows.length > 0 && assignedWorkflows[0].organisation) {
        return assignedWorkflows[0].organisation.id;
      }
      
      // If all else fails, return the organization ID from the URL params
      return organizationId;
    } catch (error) {
      console.error('Error getting current user organization ID:', error);
      return organizationId;
    }
  };

  useEffect(() => {
  
      const FetchAssignPermission = async () => {
        try {
          const token = getAuthToken();
          const apiBaseUrl = process.env.REACT_APP_API_URL || 
                               `${window.location.protocol}//${window.location.hostname}:8080`;
              
          const res = await fetch(`${apiBaseUrl}/v1/organization-config`,{
            headers: {'Authorization': `Bearer ${token}`}
          });
          if(!res.ok) return;
          const data = await res.json();
          setCanAssign(data[0].allow_workflow_self_assign);
        }
        catch (err) {
          console.error("Error checking assign permission:", err);
          setCanAssign(false);
        }
      };
  
      FetchAssignPermission();
    }, []);
  
  
  useEffect(() => {
    // First check for directly stored organizationId in localStorage
    const directOrgId = localStorage.getItem('organizationId');
    
    if (directOrgId) {
      // Use directly stored organization ID
      debugLog('Using organization ID from localStorage:', directOrgId);
      setOrganizationId(directOrgId);
    } else {
      // Fallback: Try to get from authInfo
      const userInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
      
      if (userInfo && userInfo.principalInfo && userInfo.principalInfo.organisationId) {
        // Use the organization ID from the auth info
        debugLog('Using organization ID from authInfo:', userInfo.principalInfo.organisationId);
        setOrganizationId(userInfo.principalInfo.organisationId);
      } else {
        // Last fallback: Check if organization ID is in the URL (for admin users creating requests on behalf of customers)
        const params = new URLSearchParams(window.location.search);
        const orgId = params.get('organizationId');
        if (orgId) {
          debugLog('Using organization ID from URL parameter:', orgId);
          setOrganizationId(orgId);
        }
        // workflowId is handled via initial state (preSelectWorkflowId / selectedTargetType)
      }
    }
  }, []);

  useEffect(() => {
    const fetchAssignedWorkflows = async () => {
      try {
        setLoading(true);
        // Clear any existing assignments to prevent stale data
        setAssignedWorkflows([]);
        
        // Log that we're starting the workflow fetch process
        debugLog('\n=== STARTING WORKFLOW FETCH PROCESS ===');
        debugLog('Current state of assignedWorkflows:', assignedWorkflows);
        debugLog('=====================================\n');
        
        // If organization ID is provided, use the organization-specific API
        if (organizationId) {
          debugLog(`Fetching workflow assignments for specific organization: ${organizationId}`);
          const assignments = await workflowService.getOrganizationWorkflowAssignments(organizationId);
          debugLog('Organization workflow assignments:', assignments);
          
          // Process the assignments data
          if (assignments && Array.isArray(assignments)) {
            debugLog(`Got ${assignments.length} workflow assignments`);
            
            // Create a clean array of workflow assignments
            const cleanAssignments = assignments.map(assignment => {
              // Ensure we have a valid workflow object
              if (!assignment.workflow) {
                console.warn('Assignment missing workflow object:', assignment.id);
                return {
                  ...assignment,
                  workflow: { name: 'Unknown Workflow', description: '' }
                };
              }
              return assignment;
            }).filter(assignment => assignment !== null);
            
            debugLog('Clean assignments:', cleanAssignments);
            
            // Print workflow names for debugging
            debugLog('\n=== WORKFLOW NAMES FROM ASSIGNMENTS ===');
            cleanAssignments.forEach((assignment, index) => {
              debugLog(`Workflow ${index + 1}: ${assignment.workflow?.name || 'Unnamed'}`);
            });
            debugLog('=====================================\n');
            
            // Set the state with the clean assignments
            setAssignedWorkflows(cleanAssignments);
            
            // Verify the state was updated correctly
            setTimeout(() => {
              if (assignedWorkflows.length === 0 && cleanAssignments.length > 0) {
                debugLog('State update verification: forcing update');
                setAssignedWorkflows([...cleanAssignments]);
              }
            }, 100);
          } else {
            console.warn('Invalid assignments data structure:', assignments);
            setAssignedWorkflows([]);
          }
        } else {
          // Otherwise, get assignments for the current customer's organization
          debugLog('Fetching workflow assignments for current user\'s organization');
          const assignments = await workflowService.getCustomerWorkflowAssignments();
          debugLog('Customer workflow assignments:', assignments);
          
          // Process the assignments data
          if (assignments && Array.isArray(assignments)) {
            debugLog(`Got ${assignments.length} workflow assignments`);
            
            // Create a clean array of workflow assignments
            const cleanAssignments = assignments.map(assignment => {
              // Ensure we have a valid workflow object
              if (!assignment.workflow) {
                console.warn('Assignment missing workflow object:', assignment.id);
                return {
                  ...assignment,
                  workflow: { name: 'Unknown Workflow', description: '' }
                };
              }
              return assignment;
            }).filter(assignment => assignment !== null);
            
            debugLog('Clean assignments:', cleanAssignments);
            
            // Print workflow names for debugging
            debugLog('\n=== WORKFLOW NAMES FROM ASSIGNMENTS ===');
            cleanAssignments.forEach((assignment, index) => {
              debugLog(`Workflow ${index + 1}: ${assignment.workflow?.name || 'Unnamed'}`);
            });
            debugLog('=====================================\n');
            
            // Set the state with the clean assignments
            setAssignedWorkflows(cleanAssignments);
            
            // Verify the state was updated correctly
            setTimeout(() => {
              if (assignedWorkflows.length === 0 && cleanAssignments.length > 0) {
                debugLog('State update verification: forcing update');
                setAssignedWorkflows([...cleanAssignments]);
              }
            }, 100);
          } else {
            console.warn('Invalid assignments data structure:', assignments);
            setAssignedWorkflows([]);
          }
        }
      } catch (error) {
        console.error('Error fetching assigned workflows:', error);
        setAssignedWorkflows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignedWorkflows();
  }, [organizationId]);

  // Auto-select workflow from URL param after assignments are loaded
  useEffect(() => {
    if (!preSelectWorkflowId || assignedWorkflows.length === 0) return;
    const match = assignedWorkflows.find(a => {
      const wfId = String(a.workflow?.id || a.workflowId || a.id || '');
      return wfId === String(preSelectWorkflowId);
    });
    if (match) {
      handleWorkflowTileSelect(match);
      setPreSelectWorkflowId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedWorkflows, preSelectWorkflowId]);

  // Auto-fill org profile data for ORGANIZATION workflows
  useEffect(() => {
    if (selectedTargetType !== 'ORGANIZATION' || !workflowDetails || !organizationId) return;

    const fetchOrgProfile = async () => {
      try {
        const response = await AppService.get(API_ENDPOINTS.ORG_DETAILS.BY_ORG(organizationId));
        const orgProfile = response?.data || response || {};
        console.log('Org profile loaded for auto-fill:', orgProfile);

        // Map org profile fields to workflow static field codes
        setFormData(prev => {
          const updatedInfo = { ...prev.additionalInfo };
          const fieldMap = {
            companyNameEn: orgProfile.companyNameEn || orgProfile.companyNameAr || '',
            email: orgProfile.email || '',
            mobileNumber: orgProfile.mobileNumber || orgProfile.landNumber || '',
            address: orgProfile.address || '',
            area: orgProfile.area || '',
            trnNumber: orgProfile.trnNumber || '',
            vatRegistrationNumber: orgProfile.vatRegistrationNumber || '',
            licenseNumber: orgProfile.licenseNumber || orgProfile.commercialRegistration || '',
            ownerName: orgProfile.ownerName || '',
            taxYear: orgProfile.taxYear || '',
            establishmentCardNumber: orgProfile.establishmentCardNumber || '',
            immigrationCardNumber: orgProfile.immigrationCardNumber || '',
            chamberMembershipNumber: orgProfile.chamberMembershipNumber || '',
            poBox: orgProfile.poBox || '',
          };
          // Only set values for fields that exist in the workflow's static fields
          for (const [code, value] of Object.entries(fieldMap)) {
            if (value) updatedInfo[code] = value;
          }
          return { ...prev, additionalInfo: updatedInfo };
        });
      } catch (err) {
        console.error('Error fetching org profile for workflow auto-fill:', err);
      }
    };

    fetchOrgProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTargetType, workflowDetails, organizationId]);

  // Phase 2A: fetch grouped employee documents when feature flag is enabled and employee is selected
  useEffect(() => {
    const fetchGrouped = async () => {
      // Debug: trace effect execution, flag state and selected employee
      try {
        debugLog('[GroupedDocs] Effect triggered with flag:', FEATURE_WORKFLOW_DOC_GROUPS, 'selectedEmployee:', selectedEmployee);
      } catch (e) { /* noop */ }
      if (!FEATURE_WORKFLOW_DOC_GROUPS) {
        // Ensure we clear any prior state when feature is disabled
        setGroupedDocs([]);
        setGroupedDocsError(null);
        setGroupedDocsLoading(false);
        debugLog('[GroupedDocs] Feature flag disabled, skipping grouped fetch');
        return;
      }
      const uid = selectedEmployee?.uid || selectedEmployee?.employeeId;
      if (!uid) {
        setGroupedDocs([]);
        setGroupedDocsError(null);
        setGroupedDocsLoading(false);
        debugLog('[GroupedDocs] No uid resolved from selectedEmployee, skipping grouped fetch');
        return;
      }
      try {
        setGroupedDocsLoading(true);
        setGroupedDocsError(null);
        const url = API_ENDPOINTS.EMPLOYEE_DOCUMENTS.GROUPED_BY_UID(uid);
        debugLog('[GroupedDocs] Fetching grouped documents for uid:', uid, '->', url);
        // Use AppService (axios instance) which attaches the Authorization header automatically
        const resp = await AppService.get(url);
        // Support both {data: {groups: [...]}} and {groups: [...]} response shapes
        const root = resp && typeof resp === 'object' ? resp : {};
        const dataNode = root.data && typeof root.data === 'object' ? root.data : root;
        const groups = Array.isArray(dataNode?.groups)
          ? dataNode.groups
          : (Array.isArray(root?.groups) ? root.groups : []);
        debugLog('[GroupedDocs] Groups received:', Array.isArray(groups) ? groups.length : 0, groups);
        setGroupedDocs(groups || []);
      } catch (e) {
        console.error('Failed to fetch grouped employee documents:', e);
        setGroupedDocsError(e?.message || 'Failed to load employee documents');
        setGroupedDocs([]);
      } finally {
        setGroupedDocsLoading(false);
      }
    };
    fetchGrouped();
  }, [selectedEmployee, FEATURE_WORKFLOW_DOC_GROUPS]);

  // Phase 2B: Autofill document fields from grouped employee documents into additionalInfo
  useEffect(() => {
    try {
      // Preconditions
      if (!FEATURE_WORKFLOW_DOC_GROUPS) return;
      const uid = selectedEmployee?.uid || selectedEmployee?.employeeId;
      if (!uid) return;
      if (!Array.isArray(groupedDocs) || groupedDocs.length === 0) return;

      // Build a set of document field names present in this workflow
      const docFieldNames = new Set();
      const pushIfDoc = (field) => {
        if (!field) return;
        // Coded dynamic fields are keyed by their title (like text dynamic fields), so the ticket
        // shows the readable title rather than the field_xxxx code.
        const code = (field.code || '').trim();
        const isDynamic = code.startsWith('field_');
        const name = (isDynamic ? (field.label || field.title || code) : (field.name || field.code || '')).trim();
        const lname = (name || '').toLowerCase();
        const allowlist = new Set(['passportcopy','visapage','birthcertificate','salarycertificate','labourcontract','emiratesid','photo','shinasnama','policeclearancecertificate','resume','educationalcertificate','experiencecertificate']);

        // Collect various type hints
        const typeHints = [
          field.fieldType,
          field.type,
          field.inputType,
          field.uiType,
          field.component,
        ]
          .filter(Boolean)
          .map((t) => t.toString().toLowerCase());

        // Heuristics: if any hint includes file-like semantics
        const hasFileLikeHint = typeHints.some((t) =>
          t === 'file' || t === 'document' || t === 'pdf' || t === 'image' ||
          t.includes('file') || t.includes('upload') || t.includes('document')
        );

        // Presence of file-related props can also indicate a file field
        const hasFileProps = !!(field.accept || field.allowedTypes || field.acceptedTypes || field.maxFileSize || field.fileExtensions);

        const isDocByName = allowlist.has(lname);
        const isDocByType = hasFileLikeHint || hasFileProps;

        // Exclude known text fields that should not be treated as document fields
        const textOnlyExclusions = new Set([
          'emiratesid',
          'emiratesidnumber',
          'emiratesidissuedate',
          'emiratesidexpirydate',
          'passportnumber',
          'visanumber'
        ]);

        if (name && (isDocByType || isDocByName) && !textOnlyExclusions.has(lname)) {
          docFieldNames.add(name);
        }
      };
      (workflowFields?.customerInfoFields || []).forEach(pushIfDoc);
      (workflowFields?.formFields || []).forEach(pushIfDoc);

      if (docFieldNames.size === 0) return;

      // Helper to map grouped documentType to form field name
      const mapDocTypeToField = (docType) => {
        const dt = (docType || '').toLowerCase();
        if (dt.includes('passport')) return 'passportCopy';
        if (dt.includes('visa')) return 'visaPage';
        // For Emirates ID, map file to emiratesIdCopy (text field 'emiratesId' remains number only)
        if (dt.includes('emirates') && dt.includes('id')) return 'emiratesIdCopy';
        if (dt.includes('photo') || dt.includes('picture')) return 'photo';
        if (dt.includes('birth') && dt.includes('cert')) return 'birthCertificate';
        if (dt.includes('labour') && dt.includes('contract')) return 'labourContract';
        if (dt.includes('salary') && dt.includes('cert')) return 'salaryCertificate';
        if (dt.includes('shinasnama')) return 'shinasnama';
        if (dt.includes('police')) return 'policeClearanceCertificate';
        if (dt.includes('resume')) return 'resume';
        if (dt.includes('education') && dt.includes('cert')) return 'educationalCertificate';
        if (dt.includes('experience') && dt.includes('cert')) return 'experienceCertificate';
        return null;
      };

      // Index current groups by mapped field name
      const currentGroups = (groupedDocs || []).filter(g => g?.common?.isCurrent && g?.documentGroupId);
      const groupByField = new Map();
      currentGroups.forEach(g => {
        const fieldName = mapDocTypeToField(g?.common?.documentType);
        if (fieldName) {
          if (!groupByField.has(fieldName)) groupByField.set(fieldName, []);
          groupByField.get(fieldName).push(g);
        }
      });

      debugLog('[Autofill] Detected document fields in workflow:', Array.from(docFieldNames));
      debugLog('[Autofill] Available current grouped fields:', Array.from(groupByField.keys()));

      // Prepare updated form data without overriding existing set values
      const latest = window.currentFormData || formData || { additionalInfo: {} };
      const updated = JSON.parse(JSON.stringify(latest));
      updated.additionalInfo = updated.additionalInfo || {};

      const alreadyHasValue = (name) => {
        const v = updated.additionalInfo?.[name];
        if (!v) return false;
        if (typeof v === 'object') {
          return !!(v.isReference || v.hasFile || v.path || v.id || (v.target && v.target.value));
        }
        return !!v;
      };

      const populated = new Set();

      // Populate found groups
      const autofilledNames = [];
      groupByField.forEach((groups, name) => {
        if (!docFieldNames.has(name)) return;
        if (alreadyHasValue(name)) return; // don't override

        // Multiple-instance types (educational/experience certificates) can have several current
        // subs (one per title); attach every active sub's docs. Other types use their single group.
        const isMultiInstance = name === 'educationalCertificate' || name === 'experienceCertificate';
        const groupsToUse = isMultiInstance ? groups : [groups[0]];

        // Collect all documents across the selected groups, remembering each doc's own group.
        const docEntries = [];
        groupsToUse.forEach(gr => {
          const grDocs = Array.isArray(gr?.documents) ? gr.documents : [];
          grDocs.forEach(d => docEntries.push({ d, gr }));
        });
        if (docEntries.length === 0) return;

        const firstGroup = groupsToUse[0];
        const primaryEntry = docEntries.find(e => e.d.isPrimary) || docEntries[0];
        const primary = primaryEntry.d;

        // Create reference metadata compatible with WorkflowFileField preview logic
        updated.additionalInfo[name] = {
          isReference: true,
          documentType: name,
          backendDocumentType: firstGroup?.common?.documentType || null,
          employeeUid: uid,
          id: primary.id,
          fileName: primary.fileName || firstGroup?.common?.documentType || name,
          documentGroupId: firstGroup?.documentGroupId,
          documentReferences: docEntries.map(({ d, gr }) => ({
            id: d.id,
            documentId: d.id,
            fileName: d.fileName,
            filePath: d.filePath || d.path || d.documentUrl || d.s3Key || null,
            documentType: name,
            documentGroupId: gr.documentGroupId,
            isPrimary: !!d.isPrimary,
            uploadDate: d.uploadDate,
          })),
        };
        populated.add(name);
        autofilledNames.push(name);
      });
      if (autofilledNames.length) {
        debugLog('[Autofill] Populated from grouped docs:', autofilledNames);
      }

      // Backfill related text fields (e.g., passportNumber, emiratesId, visaNumber) from grouped document numbers
      // when employee profile text fields are missing. This is non-destructive and only fills empties.
      try {
        const allFields = (workflowFields && workflowFields.allFields) || [];
        const isEmpty = (v) => v === undefined || v === null || v === '';

        // Local document field detector (avoid referencing out-of-scope helpers)
        const isDocFieldLocal = (fieldName, fieldType, fieldCategory) => {
          const lowerFieldType = (fieldType || '').toLowerCase();
          const lowerFieldCategory = (fieldCategory || '').toLowerCase();
          return (
            lowerFieldType === 'file' ||
            lowerFieldType === 'document' ||
            lowerFieldCategory === 'documents' ||
            lowerFieldCategory.includes('document') ||
            lowerFieldCategory.includes('file')
          );
        };

        groupByField.forEach((groups, fieldKey) => {
          if (!Array.isArray(groups)) return;
          groups.forEach((group) => {
            const common = group?.common || {};
            const docTypeRaw = common.documentType || group?.documentType || '';
            const docTypeLc = docTypeRaw.toLowerCase();
            // Try to extract a document number using multiple commonly used keys
            const pickFirst = (obj, keys) => {
              for (const k of keys) {
                if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return { key: k, value: obj[k] };
              }
              return null;
            };
            const preferredKeys = ['documentNumber', 'no', 'number', 'passportNumber', 'eidNumber', 'idNumber', 'cardNumber'];
            let extracted = pickFirst(common, preferredKeys) || pickFirst(group || {}, preferredKeys) ||
              pickFirst(common?.meta || {}, preferredKeys) || pickFirst(common?.details || {}, preferredKeys);

            // As a broader fallback, scan common for any key containing 'number'
            if (!extracted) {
              const cnKeys = Object.keys(common || {}).filter(k => /number|no$/i.test(k));
              for (const k of cnKeys) {
                const v = common[k];
                if (v && typeof v === 'string' && v.trim().length >= 4) { extracted = { key: k, value: v }; break; }
                if (typeof v === 'number') { extracted = { key: k, value: String(v) }; break; }
              }
            }

            // Generic mapping for Visa dates from current group (match New Ticket behavior)
            if (docTypeLc.includes('visa')) {
              const issue = common?.issueDate; // 'YYYY-MM-DD'
              const expiry = common?.expiryDate; // 'YYYY-MM-DD'
              const base = docTypeLc.replace('_', ''); // visapage -> visapage; visa -> visa

              const issueVariants = [
                'visaIssueDate',
                'visaIssue',
                `${base}IssueDate`,
                `${base}_issue_date`,
                `${docTypeLc}_issue_date`,
                `${docTypeLc}_issueDate`
              ];
              const expiryVariants = [
                'visaExpiryDate',
                'visaExpiry',
                `${base}ExpiryDate`,
                `${base}_expiry_date`,
                `${docTypeLc}_expiry_date`,
                `${docTypeLc}_expiryDate`
              ];

              const setDateIfEmpty = (fieldName, dateStr) => {
                if (!dateStr) return;
                const currentVal = updated?.additionalInfo?.[fieldName];
                if (!isEmpty(currentVal)) return;
                const d = new Date(dateStr);
                const val = isNaN(d.getTime()) ? dateStr : d;
                updated.additionalInfo[fieldName] = val;
                const f = allFields.find(ff => ff?.code === fieldName);
                if (f && f.code !== f.name) {
                  const codeVal = updated?.additionalInfo?.[f.code];
                  if (isEmpty(codeVal)) {
                    updated.additionalInfo[f.code] = val;
                  }
                }
              };

              if (issue) {
                issueVariants.forEach(v => {
                  const exists = allFields.some(ff => (ff?.name === v) || (ff?.code === v));
                  if (exists) setDateIfEmpty(v, issue);
                });
                // Always set the canonical name as well (some forms rely on this exact key)
                const curVI = updated?.additionalInfo?.visaIssueDate;
                if (isEmpty(curVI)) {
                  setDateIfEmpty('visaIssueDate', issue);
                }
                debugLog('[Autofill] Backfilled Visa issue date variants from grouped doc:', issue);
              }
              if (expiry) {
                expiryVariants.forEach(v => {
                  const exists = allFields.some(ff => (ff?.name === v) || (ff?.code === v));
                  if (exists) setDateIfEmpty(v, expiry);
                });
                // Always set the canonical name as well
                const curVE = updated?.additionalInfo?.visaExpiryDate;
                if (isEmpty(curVE)) {
                  setDateIfEmpty('visaExpiryDate', expiry);
                }
                debugLog('[Autofill] Backfilled Visa expiry date variants from grouped doc:', expiry);
              }

              // Correction pass: if visa dates were set to documentNumber earlier, replace with real dates
              try {
                const toDateObj = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? null : d; };
                const fixIfEqualsDocNo = (key, dateStr) => {
                  const cur = updated?.additionalInfo?.[key];
                  if (typeof cur === 'string' && cur === docNo && dateStr) {
                    const d = toDateObj(dateStr) || dateStr;
                    updated.additionalInfo[key] = d;
                    debugLog(`[Autofill] Corrected ${key} (visa) that was set to documentNumber; now set to date`, d);
                  }
                };
                const allIssueKeys = ['visaIssueDate', ...issueVariants];
                const allExpiryKeys = ['visaExpiryDate', ...expiryVariants];
                allIssueKeys.forEach(k => fixIfEqualsDocNo(k, issue));
                allExpiryKeys.forEach(k => fixIfEqualsDocNo(k, expiry));
              } catch (_) { /* ignore */ }
            }

            // Look into documents for a number-like field if still missing
            if (!extracted && Array.isArray(group?.documents)) {
              for (const d of group.documents) {
                const ex = pickFirst(d || {}, preferredKeys) || (() => {
                  const dk = Object.keys(d || {}).filter(k => /number|no$/i.test(k));
                  for (const k of dk) {
                    const v = d[k];
                    if (v && typeof v === 'string' && v.trim().length >= 4) return { key: k, value: v };
                    if (typeof v === 'number') return { key: k, value: String(v) };
                  }
                  return null;
                })();
                if (ex) { extracted = ex; break; }
              }
            }

            const docNo = extracted ? extracted.value : null;
            debugLog('[Grouped Backfill] docType:', docTypeRaw, 'extracted number:', docNo, extracted ? `(key: ${extracted.key})` : '');
            if (!docNo) {
              debugLog('[Grouped Backfill] common keys:', Object.keys(common || {}));
              debugLog('[Grouped Backfill] group keys:', Object.keys(group || {}));
            }
            if (!docNo) return; // nothing to backfill

            // Find candidate non-document fields that correspond to this document number
            const candidates = allFields.filter((f) => {
              if (!f) return false;
              const nameLc = (f.name || '').toLowerCase();
              const codeLc = (f.code || '').toLowerCase();
              const titleLc = (f.title || '').toLowerCase();
              // skip document/file fields
              if (isDocFieldLocal(f.name || f.code || '', f.type, f.category)) return false;

              if (docTypeLc.includes('passport')) {
                return (
                  (nameLc.includes('passport') || codeLc.includes('passport') || titleLc.includes('passport')) &&
                  (nameLc.includes('number') || codeLc.includes('number') || titleLc.includes('number'))
                );
              }
              if (
                docTypeLc.includes('emirates') ||
                docTypeLc.includes('eid') ||
                docTypeLc.includes('id card') ||
                docTypeLc.includes('idcard')
              ) {
                // emirates id field names often are just 'emiratesId' (may not have 'number')
                return (
                  (nameLc.includes('emirates') && nameLc.includes('id')) ||
                  (codeLc.includes('emirates') && codeLc.includes('id')) ||
                  (titleLc.includes('emirates') && titleLc.includes('id'))
                );
              }
              if (docTypeLc.includes('visa')) {
                return (
                  (nameLc.includes('visa') || codeLc.includes('visa') || titleLc.includes('visa')) &&
                  (nameLc.includes('number') || codeLc.includes('number') || titleLc.includes('number'))
                );
              }
              return false;
            });

            candidates.forEach((f) => {
              const nameKey = f.name;
              const codeKey = f.code;
              const nameVal = nameKey ? updated.additionalInfo?.[nameKey] : undefined;
              const codeVal = codeKey ? updated.additionalInfo?.[codeKey] : undefined;

              if (nameKey && isEmpty(nameVal)) {
                updated.additionalInfo[nameKey] = docNo;
              }
              if (codeKey && codeKey !== nameKey && isEmpty(codeVal)) {
                updated.additionalInfo[codeKey] = docNo;
              }
            });

            // Explicit direct mapping for passport number field
            if (docTypeLc.includes('passport')) {
              const currentVal = updated?.additionalInfo?.passportNumber;
              if (isEmpty(currentVal)) {
                updated.additionalInfo.passportNumber = docNo;
                // If a field exists with code 'passportNumber', mirror to it as well
                const pnField = allFields.find(ff => ff?.code === 'passportNumber');
                if (pnField && pnField.code !== pnField.name) {
                  const codeVal = updated?.additionalInfo?.[pnField.code];
                  if (isEmpty(codeVal)) {
                    updated.additionalInfo[pnField.code] = docNo;
                  }
                }
                debugLog('[Autofill] Explicit backfill passportNumber from grouped doc number:', docNo);
              }
            }

            // Explicit direct mapping for Emirates ID dates from current group (match New Ticket behavior)
            if (docTypeLc.includes('emirates') || docTypeLc.includes('eid') || docTypeLc.includes('id card') || docTypeLc.includes('idcard')) {
              const issue = common?.issueDate; // 'YYYY-MM-DD'
              const expiry = common?.expiryDate; // 'YYYY-MM-DD'
              const base = docTypeLc.replace('_', ''); // emiratesid

              // Generate field name variants similar to New Ticket
              const issueVariants = [
                'emiratesIdIssueDate',
                'emiratesIdIssue',
                `${base}IssueDate`,
                `${base}_issue_date`,
                `${docTypeLc}_issue_date`,
                `${docTypeLc}_issueDate`
              ];
              const expiryVariants = [
                'emiratesIdExpiryDate',
                'emiratesIdExpiry',
                `${base}ExpiryDate`,
                `${base}_expiry_date`,
                `${docTypeLc}_expiry_date`,
                `${docTypeLc}_expiryDate`
              ];

              const setDateIfEmpty = (fieldName, dateStr) => {
                if (!dateStr) return;
                const currentVal = updated?.additionalInfo?.[fieldName];
                if (!isEmpty(currentVal)) return;
                const d = new Date(dateStr);
                const val = isNaN(d.getTime()) ? dateStr : d;
                updated.additionalInfo[fieldName] = val;
                // Mirror by code if different
                const f = allFields.find(ff => ff?.code === fieldName);
                if (f && f.code !== f.name) {
                  const codeVal = updated?.additionalInfo?.[f.code];
                  if (isEmpty(codeVal)) {
                    updated.additionalInfo[f.code] = val;
                  }
                }
              };

              if (issue) {
                issueVariants.forEach(v => {
                  // only set for variants that exist in the form
                  const exists = allFields.some(ff => (ff?.name === v) || (ff?.code === v));
                  if (exists) setDateIfEmpty(v, issue);
                });
                debugLog('[Autofill] Backfilled Emirates ID issue date variants from grouped doc:', issue);
              }
              if (expiry) {
                expiryVariants.forEach(v => {
                  const exists = allFields.some(ff => (ff?.name === v) || (ff?.code === v));
                  if (exists) setDateIfEmpty(v, expiry);
                });
                debugLog('[Autofill] Backfilled Emirates ID expiry date variants from grouped doc:', expiry);
              }

              // If dates were incorrectly set to documentNumber earlier, fix them now
              try {
                const toDateObj = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? null : d; };
                const fixIfEqualsDocNo = (key, dateStr) => {
                  const cur = updated?.additionalInfo?.[key];
                  if (typeof cur === 'string' && cur === docNo && dateStr) {
                    const d = toDateObj(dateStr) || dateStr;
                    updated.additionalInfo[key] = d;
                    debugLog(`[Autofill] Corrected ${key} that was set to documentNumber; now set to date`, d);
                  }
                };
                issueVariants.forEach(k => fixIfEqualsDocNo(k, issue));
                expiryVariants.forEach(k => fixIfEqualsDocNo(k, expiry));
              } catch (_) { /* ignore */ }
            }
          });
        });

        debugLog('[Autofill] Backfilled text fields from grouped document numbers when profile values were empty');
      } catch (bfErr) {
        console.warn('Grouped number backfill warning:', bfErr);
      }

      // Mark missing document fields explicitly
      const notFoundNames = [];
      docFieldNames.forEach(name => {
        if (alreadyHasValue(name)) return;
        if (populated.has(name)) return;
        if (!groupByField.has(name)) {
          updated.additionalInfo[name] = { notFoundInEmployeeDocs: true };
          notFoundNames.push(name);
        }
      });
      if (notFoundNames.length) {
        debugLog('[Autofill] Marked not found in employee documents:', notFoundNames);
      }

      // If we didn't change anything, skip state update
      const changed = Object.keys(updated.additionalInfo || {}).some(k => latest?.additionalInfo?.[k] !== updated.additionalInfo[k]);
      if (!changed) return;

      // Sync name/code keys after grouped backfill (non-destructive)
      try {
        const allFieldsForSync = (workflowFields && workflowFields.allFields) || [];
        const isEmpty = (v) => v === undefined || v === null || v === '';
        allFieldsForSync.forEach((f) => {
          if (!f) return;
          const nameKey = f.name;
          const codeKey = f.code;
          if (!nameKey || !codeKey || nameKey === codeKey) return;
          const nameVal = updated?.additionalInfo?.[nameKey];
          const codeVal = updated?.additionalInfo?.[codeKey];
          if (!isEmpty(nameVal) && isEmpty(codeVal)) {
            updated.additionalInfo[codeKey] = nameVal;
          } else if (!isEmpty(codeVal) && isEmpty(nameVal)) {
            updated.additionalInfo[nameKey] = codeVal;
          }
        });
        // Targeted logs for passportNumber by name and code
        const pnField = ((workflowFields?.allFields) || []).find(f => (f.name === 'passportNumber') || (f.code === 'passportNumber'));
        if (pnField) {
          const pnName = pnField.name;
          const pnCode = pnField.code;
          debugLog('[Grouped Sync] passportNumber by name:', pnName, '->', updated.additionalInfo?.[pnName]);
          debugLog('[Grouped Sync] passportNumber by code:', pnCode, '->', updated.additionalInfo?.[pnCode]);
        }
      } catch (syncErr) {
        console.warn('Grouped name/code sync warning:', syncErr);
      }

      // Persist to state and global reference for WorkflowFileField
      window.currentFormData = updated;
      setFormData(updated);

      // Force a re-render to ensure FieldFactory picks WorkflowFileField for refs
      setTimeout(() => {
        window.currentFormData = JSON.parse(JSON.stringify(updated));
        setFormData(JSON.parse(JSON.stringify(updated)));
      }, 50);
    } catch (e) {
      console.error('Autofill from grouped employee documents failed:', e);
    }
  }, [groupedDocs, workflowFields, selectedEmployee, FEATURE_WORKFLOW_DOC_GROUPS]);

  // Removed duplicate state declarations
  
  // State to store field mappings for deduplicated fields
  const [fieldMappings, setFieldMappings] = useState({});

  // State to store field categories
  const [fieldCategories, setFieldCategories] = useState([]);
  
  // Handle workflow selection change (from dropdown - legacy)
  const handleWorkflowChange = (event) => {
    const selectedId = event.target.value;
    debugLog('Selected workflow ID:', selectedId);
    setSelectedWorkflow(selectedId);
  };

  // Handle workflow selection from tile panel
  const handleWorkflowTileSelect = (workflowItem) => {
    // workflowItem can be the assignment object with nested workflow, or just the workflow
    const assignmentId = workflowItem.id || workflowItem.assignmentId;
    debugLog('Selected workflow from tile:', workflowItem);
    debugLog('Assignment ID:', assignmentId);
    setSelectedWorkflow(assignmentId);
  };
  
  // Extract fields from workflow steps
  const extractFields = (workflowData) => {
    debugLog('Extracting fields from workflow data:', workflowData);
    
    // The definition is stored as a JSON string, so we need to parse it first
    let definition;
    try {
      if (workflowData.definition && typeof workflowData.definition === 'string') {
        definition = JSON.parse(workflowData.definition);
        debugLog('Parsed workflow definition:', definition);
      } else {
        // If definition is already an object, use it directly
        definition = workflowData.definition || {};
      }
    } catch (error) {
      console.error('Error parsing workflow definition:', error);
      definition = {};
    }
    
    // Now extract steps from the parsed definition
    const steps = definition.steps || [];
    debugLog('Extracted steps:', steps);
    
    if (!steps || !Array.isArray(steps) || steps.length === 0) {
      console.error('No steps found in workflow definition');
      return {
        customerInfoFields: [],
        formFields: [],
        allFields: []
      };
    }
    
    // Use Maps to track unique fields by their code
    const fieldMap = new Map();
    const customerInfoFieldMap = new Map();
    const formFieldMap = new Map();
    
    // Define common field categories for better organization
    const fieldCategories = {
      PERSONAL: ['name', 'first', 'last', 'middle', 'birth', 'dob', 'gender', 'sex', 'age', 'nationality', 'marital', 'person'],
      CONTACT: ['email', 'phone', 'mobile', 'fax', 'contact', 'number', 'tel', 'communication'],
      ADDRESS: ['address', 'street', 'city', 'state', 'country', 'zip', 'postal', 'location', 'region', 'province'],
      EMPLOYMENT: ['job', 'position', 'title', 'department', 'company', 'employer', 'salary', 'employee', 'work', 'occupation', 'role'],
      IDENTIFICATION: ['passport', 'id', 'license', 'national', 'identity', 'uid', 'document', 'identification', 'card', 'certificate']
    };
    
    // Helper function to determine field category based on field name
    const determineFieldCategory = (field) => {
      const fieldName = (field.name || field.code || '').toLowerCase();
      const fieldLabel = (field.label || '').toLowerCase();
      
      // If field already has a category, use it
      if (field.category) {
        // Convert legacy categories to our standard format
        if (field.category.toLowerCase() === 'personal') return 'PERSONAL_INFO';
        if (field.category.toLowerCase() === 'contact') return 'CONTACT_INFO';
        return field.category;
      }
      
      // Special case for firstName/lastName/fullName fields
      if (fieldName.includes('firstname') || fieldName.includes('first_name') || 
          fieldName.includes('lastname') || fieldName.includes('last_name') ||
          fieldName.includes('fullname') || fieldName.includes('full_name') ||
          fieldName === 'name' || fieldName === 'nameEn') {
        return 'PERSONAL_INFO';
      }
      
      // Special case for email/phone fields
      if (fieldName.includes('email') || fieldName.includes('phone') || 
          fieldName.includes('mobile') || fieldName === 'contact') {
        return 'CONTACT_INFO';
      }
      
      // Check both field name and label against common field categories
      for (const [category, keywords] of Object.entries(fieldCategories)) {
        if (keywords.some(keyword => fieldName.includes(keyword) || fieldLabel.includes(keyword))) {
          return category === 'PERSONAL' ? 'PERSONAL_INFO' : 
                 category === 'CONTACT' ? 'CONTACT_INFO' : 
                 `${category}`;
        }
      }
      
      // Default category
      return 'SERVICE_SPECIFIC';
    };
    
    // Process each step to extract fields
    steps.forEach(step => {
      debugLog(`Processing step: ${step.name} (${step.id})`);
      
      // Process regular fields in this step
      if (step.fields && Array.isArray(step.fields)) {
        step.fields.forEach(field => {
          if (field) {
            // Get a unique identifier for the field (prefer code, fallback to combination of properties)
            const fieldId = field.code || `${field.type}_${field.label || 'unlabeled'}_${field.category || 'uncategorized'}`;
            // Skip if we've already processed this field
            if (!fieldMap.has(fieldId)) {
              // Clone the field to avoid reference issues
              const processedField = { ...field };
              
              // Add step information to the field
              processedField.stepId = step.id;
              processedField.stepName = step.name;
              processedField.serviceSteps = [step.id]; // Track which steps use this field
              
              // Ensure the field has a name property (for easier reference)
              if (!processedField.name && processedField.code) {
                processedField.name = processedField.code;
              }
              
              // Determine field category using our helper function
              processedField.category = determineFieldCategory(processedField);
              
              // Determine if this is a customer info field
              const isCustomerInfoField = (
                (processedField.category && processedField.category.toLowerCase().includes('personal')) ||
                (processedField.category && processedField.category.toLowerCase().includes('contact')) ||
                (processedField.category && processedField.category.toLowerCase().includes('address')) ||
                (field.label && field.label.toLowerCase().includes('customer')) ||
                (field.code && field.code.toLowerCase().includes('customer'))
              );
              
              // Add to the appropriate maps
              fieldMap.set(fieldId, processedField);
              
              if (isCustomerInfoField) {
                customerInfoFieldMap.set(fieldId, processedField);
              } else {
                formFieldMap.set(fieldId, processedField);
              }
            } else {
              // If this field already exists, add this step to the serviceSteps array
              const existingField = fieldMap.get(fieldId);
              if (!existingField.serviceSteps.includes(step.id)) {
                existingField.serviceSteps.push(step.id);
              }
            }
          }
        });
      }
      
      // Process static fields in this step
      if (step.staticFields && Array.isArray(step.staticFields)) {
        step.staticFields.forEach(field => {
          if (field) {
            // Get a unique identifier for the field
            const fieldId = field.code || `${field.type}_static_${Math.random()}`;
            
            // Skip if we've already processed this field
            if (!fieldMap.has(fieldId)) {
              // Clone the field to avoid reference issues
              const processedField = { ...field };
              
              // Add step information to the field
              processedField.stepId = step.id;
              processedField.stepName = step.name;
              processedField.serviceSteps = [step.id];
              
              // Mark as a static field
              processedField.isStatic = true;
              
              // Ensure the field has a name property (for easier reference)
              if (!processedField.name) {
                processedField.name = processedField.code;
              }
              
              // Add label if missing (important for UI rendering)
              if (!processedField.label) {
                // Convert camelCase to Title Case (e.g., firstNameEn -> First Name En)
                processedField.label = processedField.name
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/^./, str => str.toUpperCase());
              }
              
              // Determine field category using our helper function
              processedField.category = determineFieldCategory(processedField);
              
              // Special handling for common employee fields to ensure they're properly categorized
              if (processedField.code === 'firstNameEn' || processedField.code === 'lastNameEn' || 
                  processedField.code === 'middleNameEn' || processedField.code === 'nameEn') {
                processedField.category = 'PERSONAL_INFO';
              }
              
              debugLog(`Processing static field: ${processedField.name} (${processedField.code}) with category ${processedField.category}`);
              
              // Add to the appropriate maps
              fieldMap.set(fieldId, processedField);
              
              // Check if this is a personal/contact field that should go into customer info
              const isPersonalField = (
                processedField.category === 'PERSONAL_INFO' || 
                processedField.category === 'CONTACT_INFO' || 
                processedField.category === 'ADDRESS'
              );
              
              if (isPersonalField) {
                customerInfoFieldMap.set(fieldId, processedField);
              } else {
                formFieldMap.set(fieldId, processedField);
              }
            } else {
              // If this field already exists, add this step to the serviceSteps array
              const existingField = fieldMap.get(fieldId);
              if (!existingField.serviceSteps.includes(step.id)) {
                existingField.serviceSteps.push(step.id);
              }
            }
          }
        });
      }
    });
    
    // Convert the maps back to arrays
    const allFields = Array.from(fieldMap.values());
    const customerInfoFields = Array.from(customerInfoFieldMap.values());
    const formFields = Array.from(formFieldMap.values());
    
    debugLog('Extracted fields (deduplicated):', {
      allFields: allFields.length,
      customerInfoFields: customerInfoFields.length,
      formFields: formFields.length
    });
    
    return {
      customerInfoFields,
      formFields,
      allFields
    };
  };
  
  // useEffect to fetch workflow details when selectedWorkflow changes
  useEffect(() => {
    debugLog('[WorkflowEffect] Triggered. selectedWorkflow:', selectedWorkflow);
    if (!selectedWorkflow) {
      debugLog('[WorkflowEffect] No workflow selected, skipping details fetch');
      return;
    }
    
    debugLog('[WorkflowEffect] Fetching workflow details for assignment ID:', selectedWorkflow);
    setLoading(true);
    
    // First, get the workflow assignment to find the workflow ID
    AppService.get(`/v1/workflow-assignments/customer`)
      .then(assignments => {
        debugLog('[WorkflowEffect] Fetched assignments list (count):', Array.isArray(assignments) ? assignments.length : 'not-array', assignments);
        // Check if there are any assignments at all
        if (!Array.isArray(assignments) || assignments.length === 0) {
          setLoading(false);
          setError({
            message: 'No workflow assignments available',
            details: 'Your organization does not have any workflow assignments yet. Please contact your administrator to assign workflows to your organization.',
            status: 404
          });
          return null;
        }
        
        // Find the matching assignment
        const matchingAssignment = assignments.find(a => a.id === selectedWorkflow);
          
        if (!matchingAssignment || !matchingAssignment.workflow || !matchingAssignment.workflow.id) {
          setLoading(false);
          setError({
            message: 'Workflow not found',
            details: 'The selected workflow assignment could not be found or is missing required information.',
            status: 404
          });
          return null;
        }
        
        debugLog('[WorkflowEffect] Found matching assignment:', matchingAssignment);
        
        const workflowId = matchingAssignment.workflow.id;
        debugLog('[WorkflowEffect] Extracted workflow ID:', workflowId);
        
        // Now fetch the detailed workflow data using the workflow ID
        return AppService.get(`/v1/workflow/${workflowId}`);
      })
      .catch(error => {
        debugLog('[WorkflowEffect] Error during assignments or workflow fetch:', error);
        setError({
          message: 'Failed to fetch workflow details',
          details: error.message,
          status: error.response?.status
        });
        setLoading(false);
        return null;
      })
      .then(workflowData => {
        if (!workflowData) {
          // This will happen if there was an error and we returned null from the catch block
          return;
        }
        
        debugLog('[WorkflowEffect] Workflow details received:', workflowData);
        
        // Parse the workflow definition to get steps
        let parsedWorkflow = workflowData;
        
        try {
          if (workflowData.definition && typeof workflowData.definition === 'string') {
            const parsed = JSON.parse(workflowData.definition);
            // If the parsed object itself has steps at top-level, use it directly as the definition
            const hasTopLevelSteps = Array.isArray(parsed?.steps);
            parsedWorkflow = {
              ...workflowData,
              definition: hasTopLevelSteps ? parsed : (parsed.definition || parsed.definitionJson || parsed.definitionObject || {}),
              type: parsed.type ?? workflowData.type,
              variant: parsed.variant ?? workflowData.variant
            };
            debugLog('[WorkflowEffect] Parsed definition summary:', {
              parsedKeys: Object.keys(parsed || {}),
              hasTopLevelSteps,
              stepsLength: hasTopLevelSteps ? parsed.steps.length : (parsed?.definition?.steps ? parsed.definition.steps.length : 0)
            });
          }
        } catch (error) {
          console.error('Error parsing workflow definition:', error);
        }
        
        // Normalize steps to ensure UI can render them consistently
        const normalizedSteps = (
          parsedWorkflow.steps ||
          (parsedWorkflow.definition && parsedWorkflow.definition.steps) ||
          (parsedWorkflow.definition && parsedWorkflow.definition.definition && parsedWorkflow.definition.definition.steps) ||
          []
        );

        if (!Array.isArray(normalizedSteps) || normalizedSteps.length === 0) {
          debugLog('[Workflow] No steps found on top-level; checked definition as well.');
        } else {
          debugLog('[Workflow] Normalized steps count:', normalizedSteps.length, {
            first: normalizedSteps[0]
          });
        }

        // Update workflow details state with the enhanced data and normalized steps
        setWorkflowDetails({ ...parsedWorkflow, steps: normalizedSteps });
        
        // Extract and organize fields from the workflow
        const fieldsObj = extractFields(workflowData);
        debugLog('[WorkflowEffect] Extracted fields summary:', {
          all: fieldsObj?.allFields?.length,
          form: fieldsObj?.formFields?.length,
          customerInfo: fieldsObj?.customerInfoFields?.length
        });
        
        // Set the workflow fields state
        setWorkflowFields(fieldsObj);
        
        // Initialize form data with empty values for all fields
        const initialFormData = {
          additionalInfo: {}
        };
        
        // Initialize all fields with empty values
        if (fieldsObj.allFields && Array.isArray(fieldsObj.allFields)) {
          fieldsObj.allFields.forEach(field => {
            if (field.name) {
              initialFormData.additionalInfo[field.name] = '';
            }
          });
        }
                // If an employee is already selected, populate the form with employee data
        if (selectedEmployee) {
          debugLog('Employee already selected, populating form data with employee information');
          const employeeUid = selectedEmployee.uid;
          const employeeId = selectedEmployee.employeeId || selectedEmployee.id;
          
          // Handle various name formats
          let firstName = selectedEmployee.firstNameEn || selectedEmployee.firstName || '';
          let lastName = selectedEmployee.lastNameEn || selectedEmployee.lastName || '';
          let fullName = selectedEmployee.nameEn || selectedEmployee.fullName || '';
          
          // If we have first/last name but no full name, construct it
          if (!fullName && (firstName || lastName)) {
            fullName = `${firstName} ${lastName}`.trim();
          }
          // If we have full name but no first/last name, try to split it
          else if (fullName && (!firstName || !lastName)) {
            const nameParts = fullName.split(' ');
            if (nameParts.length > 1) {
              if (!firstName) firstName = nameParts[0];
              if (!lastName) lastName = nameParts.slice(1).join(' ');
            }
          }
          
          // Add employee identification to form data
          initialFormData.assignedEmployeeUid = employeeUid;
          initialFormData.assignedEmployeeId = employeeId;
          initialFormData.employeeName = fullName;
          
          debugLog(`Populating form with employee data - Name: ${fullName}, ID: ${employeeId}, UID: ${employeeUid}`);
          debugLog(`First Name: ${firstName}, Last Name: ${lastName}`);
          
          // Map employee data to form fields
          fieldsObj.allFields.forEach(field => {
            const fieldName = field.name || field.code || '';
            const fieldCode = field.code || '';
            const fieldCategory = field.category || '';
            
            if (!fieldName) return;
            
            debugLog(`Checking field for employee data mapping: ${fieldName} (${fieldCode}) - Category: ${fieldCategory}`);
            
            // Special handling for static fields with exact code matches
            if (field.isStatic) {
              if (fieldCode === 'firstNameEn') {
                initialFormData.additionalInfo[fieldName] = firstName;
                debugLog(`Static field match: ${fieldName} = ${firstName}`);
                return;
              }
            }
          });
        }
        
        // Set loading to false
        setLoading(false);
        setError(null); // Clear any previous errors
        
        // Calculate and initialize service costs
        const initialCosts = calculateServiceCosts();
        setServiceCosts(initialCosts);
        debugLog('Initial service costs:', initialCosts);
        
        // Enable services if there are any steps in the normalized workflow
        setServicesEnabled(Array.isArray(normalizedSteps) && normalizedSteps.length > 0);
        
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching workflow details:', error);
        setError({
          message: 'Failed to load workflow details',
          details: error.message
        });
        setLoading(false);
      });
  }, [selectedWorkflow, selectedEmployee]);
  
  // Debug: log whenever workflowDetails changes to confirm steps presence
  useEffect(() => {
    if (!workflowDetails) {
      console.log('[WorkflowDetailsEffect] workflowDetails is null');
      return;
    }
    const steps = workflowDetails?.steps;
    console.groupCollapsed('[WorkflowDetailsEffect] workflowDetails updated');
    console.log('hasWorkflowDetails:', !!workflowDetails);
    console.log('hasStepsArray:', Array.isArray(steps));
    console.log('stepsLength:', Array.isArray(steps) ? steps.length : 'n/a');
    if (Array.isArray(steps) && steps.length > 0) {
      console.log('firstStep:', { id: steps[0].id, name: steps[0].name || steps[0].serviceName });
    }
    console.groupEnd();
  }, [workflowDetails]);
  
  // Handle form input changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    
    // Update global form data reference for WorkflowFileField component
    window.currentFormData = updatedFormData;
    setFormData(updatedFormData);
  };
  
  // Handle customer UID changes
  const handleCustomerUidChange = (value) => {
    setFormData(prev => ({
      ...prev,
      customerUid: value
    }));
    
    // Reset validation if the UID changes
    if (customerUidValidation.success) {
      setCustomerUidValidation(prev => ({
        ...prev,
        error: null,
        success: false,
        customerInfo: null
      }));
    }
  };
  
  // Validate customer UID
  const validateCustomerUid = async (uid) => {
    if (!uid) return;
    
    setCustomerUidValidation(prev => ({ ...prev, validating: true, error: null }));
    
    try {
      // Make API call to validate the customer UID using the lookup endpoint
      const response = await AppService.get(`/api/customers/lookup?uid=${uid}`);
      
      if (response && response.uid) {
        // Success - customer found
        const customerData = response;
        debugLog('Customer data received:', customerData);
        
        // Update customer validation state
        setCustomerUidValidation({
          error: null,
          success: true,
          customerInfo: {
            id: customerData.organizationId || '',
            name: customerData.nameEn || `${customerData.firstNameEn || ''} ${customerData.lastNameEn || ''}`,
            organization: customerData.organizationName || '',
            email: customerData.email || ''
          },
          validating: false
        });

        // Phase 2B: Autofill workflow additionalInfo from CUSTOMER profile data (not documents)
        try {
          const allFields = (workflowFields && (workflowFields.allFields || workflowFields)) || [];
          const mappedFromCustomer = mapCustomerDataToWorkflowFields(customerData, allFields);
          debugLog('Mapped customer values for autofill:', mappedFromCustomer);

          if (mappedFromCustomer && Object.keys(mappedFromCustomer).length > 0) {
            setFormData(prev => {
              const next = { ...prev, additionalInfo: { ...(prev.additionalInfo || {}) } };
              // Respect existing user-entered values; only fill empties
              Object.entries(mappedFromCustomer).forEach(([key, val]) => {
                const existing = next.additionalInfo[key];
                const isEmpty = existing === undefined || existing === null || existing === '';
                if (isEmpty && val !== undefined && val !== null && `${val}`.trim() !== '') {
                  next.additionalInfo[key] = val;
                }
              });
              // Update global form data reference for WorkflowFileField component
              window.currentFormData = next;
              return next;
            });
          }
        } catch (mapErr) {
          console.error('Error mapping customer data to workflow fields:', mapErr);
        }
      } else {
        // No customer found with this UID
        handleCustomerNotFound(uid);
      }
    } catch (error) {
      console.error('Error validating customer UID:', error);
      // Check if it's a 404 error (customer not found)
      if (error.response && error.response.status === 404) {
        // Handle customer not found - show dialog to create new customer
        handleCustomerNotFound(uid);
      } else {
        // Other error
        setCustomerUidValidation({
          error: 'Error validating customer UID',
          success: false,
          customerInfo: null,
          validating: false
        });
      }
    }
  };
  
  // Handle customer not found - show dialog to create new customer
  const handleCustomerNotFound = (uidValue) => {
    debugLog("HANDLING CUSTOMER NOT FOUND FOR UID:", uidValue);
    
    // Reset customer UID validation state
    setCustomerUidValidation({
      error: 'Customer not found with this UID',
      success: false,
      customerInfo: null,
      validating: false
    });
    
    // Initialize new customer data
    const newCustomerData = {
      uid: uidValue,
      firstNameEn: "",
      middleNameEn: "",
      lastNameEn: "",
      nameEn: "",
      mobileNumber: "",
      email: ""
    };
    
    // Update state with new customer data
    setNewCustomer(newCustomerData);
    
    // Open the customer creation dialog
    setTimeout(() => {
      setOpenCustomerDialog(true);
    }, 100);
  };

  // Handle customer dialog input changes
  const handleCustomerInputChange = (e) => {
    const { name, value } = e.target;
    
    // Update the specific field
    const updatedCustomer = {
      ...newCustomer,
      [name]: value
    };
    
    // If first, middle, or last name changed, auto-update the full name
    if (name === 'firstNameEn' || name === 'middleNameEn' || name === 'lastNameEn') {
      const fullName = [
        name === 'firstNameEn' ? value : updatedCustomer.firstNameEn,
        name === 'middleNameEn' ? value : updatedCustomer.middleNameEn,
        name === 'lastNameEn' ? value : updatedCustomer.lastNameEn
      ].filter(Boolean).join(' ');
      
      updatedCustomer.nameEn = fullName;
    }
    
    setNewCustomer(updatedCustomer);
  };
  
  // Handle customer dialog close
  const handleCloseCustomerDialog = () => {
    setOpenCustomerDialog(false);
  };
  
  // Handle customer creation
  const handleCreateCustomer = () => {
    // Validate required fields
    if (!newCustomer.firstNameEn.trim()) {
      dispatch(showErroMessage("First name is required"));
      return;
    }
    
    if (!newCustomer.lastNameEn.trim()) {
      dispatch(showErroMessage("Last name is required"));
      return;
    }
    
    if (!newCustomer.mobileNumber.trim()) {
      dispatch(showErroMessage("Mobile number is required"));
      return;
    }
    
    // If nameEn is not provided, generate it from first, middle and last name
    let customerData = { ...newCustomer };
    if (!customerData.nameEn.trim()) {
      const fullName = [
        customerData.firstNameEn.trim(),
        customerData.middleNameEn.trim(),
        customerData.lastNameEn.trim()
      ].filter(Boolean).join(" ");
      
      customerData.nameEn = fullName;
    }
    
    dispatch(showLoader());
    
    debugLog("Creating customer with UID:", customerData.uid);
    
    // Call the API to create a new customer
    AppService.post("/api/customers/create", customerData)
      .then((response) => {
        debugLog("Customer created successfully:", response);
        dispatch(showSuccessMessage(`Customer ${customerData.firstNameEn} ${customerData.lastNameEn} created successfully with UID: ${customerData.uid}`));
        
        // Close the dialog
        setOpenCustomerDialog(false);
        
        // Update the form data with the newly created customer
        setFormData(prevData => ({
          ...prevData,
          customerUid: customerData.uid
        }));
        
        // Update customer validation state
        setCustomerUidValidation({
          error: null,
          success: true,
          customerInfo: {
            id: response.organizationId || '',
            name: response.nameEn || `${response.firstNameEn || ''} ${response.lastNameEn || ''}`,
            organization: response.organizationName || '',
            email: response.email || ''
          },
          validating: false
        });
        
        dispatch(dismissLoader());
      })
      .catch((error) => {
        console.error("Error creating customer:", error);
        dispatch(showErroMessage("Failed to create customer. Please try again."));
        dispatch(dismissLoader());
      });
  };

  // Utility function to detect if a field is a dynamic service field (UUID pattern)
  const isDynamicServiceField = (fieldName) => {
    // UUID pattern regex
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(fieldName) ||
           /^field_[0-9a-f]{8}$/i.test(fieldName); // Also match field_XXXXXXXX pattern
  };

  // Handle change in additional info fields
  const handleAdditionalInfoChange = (key, value, fieldType = 'TEXT') => {
    debugLog(`Updating field ${key} with value:`, value, `(type: ${fieldType})`);
    
    // Check if this is a dynamic service field
    const isDynamic = isDynamicServiceField(key);
    if (isDynamic) {
      debugLog(`Field ${key} identified as dynamic service field`);
    }
    
    // Create a deep clone of the current form data to avoid reference issues
    const updatedFormData = JSON.parse(JSON.stringify(formData));
    
    // Ensure additionalInfo object exists
    if (!updatedFormData.additionalInfo) {
      updatedFormData.additionalInfo = {};
    }
    
    // Update form field values
    // Special handling for file type fields
    // File fields need to store the File object, not a string
    if (fieldType === 'FILE' || fieldType === 'PDF' || fieldType === 'DOCUMENT') {
      // For file fields, value should already be a File object from the FileUploadField component
      // We can't JSON stringify a File object, so we need to handle it separately
      debugLog(`Setting file field ${key}`);
      
      // Update the form data directly
      setFormData(prev => {
        const newFormData = {
          ...prev,
          additionalInfo: {
            ...prev.additionalInfo,
            [key]: value // Store the File object directly
          }
        };
        
        // Ensure dynamic field codes are properly synchronized
        if (isDynamic) {
          debugLog(`Synchronizing dynamic field ${key} in form data`);
          // Create a reference in window.currentFormData for external components
          if (window.currentFormData) {
            window.currentFormData.additionalInfo = window.currentFormData.additionalInfo || {};
            window.currentFormData.additionalInfo[key] = value;
          }
        }
        
        debugLog(`Updated form data for file field ${key}:`, newFormData);
        return newFormData;
      });
    } else {
      // For all other field types
      updatedFormData.additionalInfo[key] = value;
      
      debugLog(`Setting field ${key} to:`, value);
      
      // Special handling for dynamic service fields
      if (isDynamic) {
        debugLog(`Synchronizing dynamic field ${key} in form data`);
        // Ensure the value is properly stored and synchronized
        window.currentFormData = window.currentFormData || { additionalInfo: {} };
        window.currentFormData.additionalInfo = window.currentFormData.additionalInfo || {};
        window.currentFormData.additionalInfo[key] = value;
      }
      
      debugLog('Updated form data:', updatedFormData);
      
      // Update the form data
      setFormData(updatedFormData);
      
      // Force a UI refresh after a short delay to ensure React detects the change
      setTimeout(() => {
        debugLog(`Refreshing UI after setting ${key}`);
        setFormData(currentFormData => {
          // Ensure dynamic fields are still present
          if (isDynamic && window.currentFormData?.additionalInfo?.[key] !== undefined) {
            return {
              ...currentFormData,
              additionalInfo: {
                ...currentFormData.additionalInfo,
                [key]: window.currentFormData.additionalInfo[key]
              }
            };
          }
          return { ...currentFormData };
        });
      }, 50);
    }
    
    // Validate the field if a value is provided
    if (value !== undefined && value !== null) {
      validateField(key, value, fieldType);
    } else {
      // Clear validation error if value is empty
      setValidationErrors(prev => ({
        ...prev,
        [key]: ''
      }));
    }
  };
  
  // Determine the field type for FieldFactory component
  const getFieldType = (field) => {
    try {
      if (!field) {
        console.warn('getFieldType called with null or undefined field');
        return 'text';
      }
      
      // Handle special types first
      if (field.type === 'PDF' || (field.code && field.code.toLowerCase().includes('doc'))) {
        return 'file';
      }

      // Detect document envelope in current bound value and treat as a file/document field
      try {
        const keyName = (field.name || field.code || '').toString();
        const boundVal = formData?.additionalInfo?.[keyName];
        const looksLikeDoc = !!(
          boundVal && typeof boundVal === 'object' && (
            boundVal.filePath || boundVal.fileName || boundVal.hasFile ||
            (boundVal.target && boundVal.target.value && (boundVal.target.value.filePath || boundVal.target.value.fileName || boundVal.target.value.documentUrl)) ||
            boundVal.documentUrl
          )
        );
        if (looksLikeDoc) {
          return 'file';
        }
      } catch (e) { /* ignore envelope detection errors */ }
      
      // Get field identifiers for checking, with safe fallbacks
      let fieldName = '';
      let fieldLabel = '';
      let fieldCode = '';
      
      // Safely get field identifiers
      try { fieldName = (field.name || '').toLowerCase(); } catch (e) { fieldName = ''; }
      try { fieldLabel = (field.label || '').toLowerCase(); } catch (e) { fieldLabel = ''; }
      try { fieldCode = (field.code || '').toLowerCase(); } catch (e) { fieldCode = ''; }
      
      // Get the best identifier to use for checks
      const identifierToCheck = fieldName || fieldLabel || fieldCode || '';
      
      // Check for Date fields. Match the backend's date validation, which treats any field whose
      // key contains "date" as a date. Check name, label AND code so a coded field titled e.g.
      // "Joining Date" renders a date picker and produces a YYYY-MM-DD value, instead of a free-text
      // input that the backend then rejects as an invalid date.
      const dateIdentifiers = `${fieldName} ${fieldLabel} ${fieldCode}`;
      if (dateIdentifiers.includes('date') || dateIdentifiers.includes('dob')) {
        return 'date';
      }
      
      // Check for Gender fields
      if (identifierToCheck.includes('gender') || 
          identifierToCheck.includes('sex')) {
        return 'select';
      }
      
      // Check field type property
      let fieldType = '';
      try {
        fieldType = ((field.fieldType || field.type || '')).toString().toUpperCase();
      } catch (e) {
        fieldType = '';
      }
      
      // Determine field type based on fieldType property
      switch(fieldType) {
        case 'DATE':
          return 'date';
        case 'EMAIL':
          return 'email';
        case 'PHONE':
        case 'TEL':
          return 'tel';
        case 'ADDRESS':
        case 'TEXTAREA':
          return 'textarea';
        case 'NUMBER':
          return 'number';
        case 'SELECT':
        case 'DROPDOWN':
          return 'select';
        case 'FILE':
        case 'PDF':
        case 'DOCUMENT':
        case 'IMAGE':
          return 'file';
        case 'BOOLEAN':
        case 'CHECKBOX':
          return 'checkbox';
      }
      
      // Default to text for unknown field types
      return 'text';
    } catch (error) {
      console.error('Error in getFieldType:', error);
      return 'text'; // Fallback to text input if any error occurs
    }
  };
  
  const handleDateChange = (key, date) => {
    if (date && !isNaN(date.getTime())) {
      // Format the date as ISO string (YYYY-MM-DD)
      const formattedDate = date.toISOString().split('T')[0];
      handleAdditionalInfoChange(key, formattedDate, 'DATE');
    } else {
      // Handle invalid date
      handleAdditionalInfoChange(key, '', 'DATE');
    }
  };

  // Persist documents uploaded directly in this form (raw S3 uploads, not references to existing
  // employee documents) back to the employee's document profile, then swap the field value for a
  // document reference. This (a) keeps the employee profile up to date and (b) makes the file show
  // in the ticket's Documents section exactly like auto-filled docs (both rely on the reference
  // carrying a documentGroupId, which the backend links to the ticket). Failures never block submit —
  // the raw upload is left as-is. Multiple-instance certificates need a per-certificate title we
  // don't have in this form, so they're skipped.
  const syncNewUploadsToEmployeeDocuments = async () => {
    try {
      if (selectedTargetType === 'ORGANIZATION') return; // employee documents only
      const uid = selectedEmployee?.uid || selectedEmployee?.employeeId || formData.employeeUid;
      if (!uid) return;

      const info = formData.additionalInfo || {};
      const multiInstance = new Set(['educationalCertificate', 'experienceCertificate']);
      const lastSeg = (p) => (typeof p === 'string' ? p.split('/').pop() : '') || 'Document';
      const bulkUrl = API_ENDPOINTS.EMPLOYEE_DOCUMENTS.GROUPED_BY_UID(uid).replace('/grouped', '/bulk');

      for (const [code, rawValue] of Object.entries(info)) {
        // File-type values may be stored nested as { target: { value } } (auto-fill stores top-level);
        // unwrap so both shapes are handled the same. Rewritten references below are set top-level,
        // which is the shape the backend expects (same as auto-fill).
        const value = (rawValue && typeof rawValue === 'object' && rawValue.target && typeof rawValue.target === 'object' && 'value' in rawValue.target)
          ? rawValue.target.value : rawValue;
        // Only raw uploads / certificate-popup values that are NOT already an employee-doc reference.
        if (!value || typeof value !== 'object' || value.isReference) continue;

        // Multiple-instance certificate value from the certificates popup: bulk-save each titled sub as
        // its own group, then reference every saved file (plus any pre-existing certs) on the ticket.
        if (value.isMultiCertificate && Array.isArray(value.subs)) {
          const certBackend = value.backendDocumentType || DOCUMENT_TYPE_MAPPING[code];
          if (!certBackend) continue;
          const allRefs = Array.isArray(value.existingRefs) ? [...value.existingRefs] : [];
          let firstGroupId = allRefs.length ? allRefs[0].documentGroupId : null;
          let primaryDoc = null;
          for (const sub of value.subs) {
            const title = (sub.title || '').trim();
            const files = Array.isArray(sub.files) ? sub.files.filter(f => f.filePath) : [];
            if (!title || files.length === 0) continue;
            try {
              const resp = await AppService.post(bulkUrl, {
                documentType: certBackend,
                replaceExisting: false, // add as a new titled certificate (its group becomes current)
                title,
                isReplace: false,
                // Use the certificate name as the saved file name (matching the employee-detail page),
                // suffixing when a certificate holds more than one file.
                documents: files.map((f, idx) => ({
                  filePath: f.filePath,
                  fileName: files.length > 1 ? `${title} (${idx + 1})` : title,
                  contentType: f.contentType || null,
                  fileSize: f.fileSize || null,
                })),
              });
              const saved = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : (resp?.data?.data || []));
              const docs = Array.isArray(saved) ? saved : [];
              docs.forEach(d => {
                allRefs.push({
                  id: d.id,
                  documentId: d.id,
                  fileName: d.fileName,
                  filePath: d.filePath,
                  documentType: code,
                  documentGroupId: d.documentGroupId,
                  isPrimary: !!d.isPrimary,
                });
                if (!firstGroupId) firstGroupId = d.documentGroupId;
                if (!primaryDoc) primaryDoc = d;
              });
              debugLog(`[WorkflowRequest] Saved certificate "${title}" for ${code} (group ${docs[0] ? docs[0].documentGroupId : 'n/a'})`);
            } catch (e) {
              console.warn(`[WorkflowRequest] Could not save certificate "${title}" for ${code}; it will be omitted`, e);
            }
          }
          if (allRefs.length > 0) {
            info[code] = {
              isReference: true,
              documentType: code,
              backendDocumentType: certBackend,
              employeeUid: uid,
              id: primaryDoc ? primaryDoc.id : allRefs[0].id,
              fileName: primaryDoc ? primaryDoc.fileName : allRefs[0].fileName,
              documentGroupId: firstGroupId,
              documentReferences: allRefs,
            };
          }
          continue;
        }

        const filePath = value.path || value.filePath;
        if (!filePath) continue;
        const backendType = DOCUMENT_TYPE_MAPPING[code];
        if (!backendType || multiInstance.has(code)) continue; // only known single-instance employee doc types

        try {
          const payload = {
            documentType: backendType,
            replaceExisting: true, // the freshly uploaded file becomes the employee's current version
            documents: [{
              filePath,
              fileName: value.fileName || lastSeg(filePath),
              contentType: value.contentType || null,
              fileSize: value.fileSize || null,
            }],
          };
          const resp = await AppService.post(bulkUrl, payload);
          const saved = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : (resp?.data?.data || []));
          const docs = Array.isArray(saved) ? saved : [];
          if (docs.length === 0) continue;
          const primary = docs.find(d => d.isPrimary) || docs[0];

          info[code] = {
            isReference: true,
            documentType: code,
            backendDocumentType: backendType,
            employeeUid: uid,
            id: primary.id,
            fileName: primary.fileName || value.fileName || lastSeg(filePath),
            documentGroupId: primary.documentGroupId,
            documentReferences: docs.map(d => ({
              id: d.id,
              documentId: d.id,
              fileName: d.fileName,
              filePath: d.filePath,
              documentType: code,
              documentGroupId: d.documentGroupId,
              isPrimary: !!d.isPrimary,
            })),
          };
          debugLog(`[WorkflowRequest] Saved uploaded ${code} to employee ${uid} profile (group ${primary.documentGroupId})`);
        } catch (e) {
          // Keep the raw upload value; don't block submission on a write-back failure.
          console.warn(`[WorkflowRequest] Could not save uploaded ${code} to employee profile; submitting as a plain upload`, e);
        }
      }

      if (window.currentFormData && window.currentFormData.additionalInfo) {
        window.currentFormData.additionalInfo = { ...window.currentFormData.additionalInfo, ...info };
      }
    } catch (e) {
      console.warn('[WorkflowRequest] Document write-back skipped due to error', e);
    }
  };

  const handleSubmit = async () => {
    if (!selectedWorkflow || !workflowDetails) return;

    try {
      // Validate all fields before submission
      const { isValid, errors: fieldErrors } = validateAllFields();
      if (!isValid) {
        // Build a list of the specific fields that failed, e.g. "policeClearanceCertificate: Police Clearance Certificate is required"
        const failedFields = Object.entries(fieldErrors)
          .filter(([, msg]) => !!msg)
          .map(([field, msg]) => `${field}: ${msg}`);

        // Log the exact expected fields that are missing/invalid so it's visible in the console
        console.warn('[WorkflowRequest] Form validation failed. Missing/invalid fields:', failedFields);
        debugLog('Form validation failed:', fieldErrors);

        const errormsg = "One or more fields have invalid or missing values.\n" + failedFields.join('\n');
        setError({
          message: 'Please correct the errors in the form before submitting',
          details: errormsg
        });
        return;
      }
      
      // Validate customer UID if provided
      if (formData.customerUid && !customerUidValidation.success) {
        setError({
          message: 'Please validate the customer UID before submitting',
          details: 'The customer UID has not been validated'
        });
        return;
      }
      
      setSubmitting(true);
      setError(null); // Clear any previous errors
      // Global overlay loader for the whole submit (document bulk-save write-back + request POST).
      dispatch(showLoader());

      // Save any documents uploaded directly in this form to the employee's profile and convert them
      // to references, so they update the profile and appear in the ticket's Documents section.
      await syncNewUploadsToEmployeeDocuments();

      // DEBUG: Log complete formData structure
      debugLog('=== COMPLETE FORM DATA STRUCTURE ===');
      debugLog('formData:', JSON.stringify(formData, null, 2));
      debugLog('=== END COMPLETE FORM DATA STRUCTURE ===');
      
      // Check for any dynamic fields in window.currentFormData that might be missing in formData
      if (window.currentFormData && window.currentFormData.additionalInfo) {
        const dynamicFields = Object.keys(window.currentFormData.additionalInfo).filter(isDynamicServiceField);
        debugLog(`Found ${dynamicFields.length} dynamic fields in window.currentFormData`);
        
        if (dynamicFields.length > 0) {
          debugLog('Dynamic fields:', dynamicFields);
          // Ensure all dynamic fields are synchronized
          dynamicFields.forEach(fieldKey => {
            if (window.currentFormData.additionalInfo[fieldKey] !== undefined && 
                formData.additionalInfo[fieldKey] === undefined) {
              debugLog(`Synchronizing missing dynamic field ${fieldKey} from window.currentFormData`);
              formData.additionalInfo[fieldKey] = window.currentFormData.additionalInfo[fieldKey];
            }
          });
        }
      }

      // Prepare request data
      // Create step assignments data from the various assignment state variables
      const stepAssignments = {};
      
      // Process the workflow steps and add assignment information
      if (workflowDetails && workflowDetails.steps) {
        workflowDetails.steps.forEach(step => {
          const stepId = step.id;
          const assignmentType = stepAssignmentTypes[stepId] || 'THAKLIS'; // Default to THAKLIS if not specified
          
          // Just send the assignment type string, not a complex object
          // This matches what the backend expects in WorkflowRequestController.java
          stepAssignments[stepId] = assignmentType.toUpperCase();
          
          // Note: We're no longer sending additional metadata like assignedAt, userId, etc.
          // The backend is only looking for a simple string value (THAKLIS or ORGANIZATION)
        });
      }
      
      // Process customer fields - convert department and position objects to strings
      const processedCustomerFields = {};
      Object.entries(formData.additionalInfo || {}).forEach(([key, value]) => {
        // Handle department objects
        if ((key === 'department' || key.toLowerCase().includes('department')) && 
            value && typeof value === 'object' && value.departmentName) {
          processedCustomerFields[key] = value.departmentName;
          debugLog(`Converted department object to string for field ${key}:`, value.departmentName);
        }
        // Handle position objects
        else if ((key === 'position' || key.toLowerCase().includes('position')) && 
                 value && typeof value === 'object' && value.positionName) {
          processedCustomerFields[key] = value.positionName;
          debugLog(`Converted position object to string for field ${key}:`, value.positionName);
        }
        // Keep all other values as-is
        else {
          processedCustomerFields[key] = value;
        }
      });
      debugLog('Customer fields for submission:', processedCustomerFields);
      
      // DEBUG: Check specifically for dynamic service field field_b7125201
      debugLog('=== DYNAMIC SERVICE FIELD DEBUG ===');
      debugLog('Looking for field_b7125201 in formData.additionalInfo:', !!processedCustomerFields['field_b7125201']);
      if (processedCustomerFields['field_b7125201']) {
        debugLog('field_b7125201 value:', processedCustomerFields['field_b7125201']);
        debugLog('field_b7125201 type:', typeof processedCustomerFields['field_b7125201']);
      } else {
        debugLog('field_b7125201 NOT FOUND in formData.additionalInfo');
        debugLog('Available fields in formData.additionalInfo:', Object.keys(processedCustomerFields));
      }
      debugLog('=== END DYNAMIC SERVICE FIELD DEBUG ===');
      
      // PHASE 2 FIX: Create proper workflow step structure with dynamic fields
      // Map dynamic fields to the expected workflow step structure
      const workflowStepFields = {};
      
      // Process workflow steps and map dynamic fields to step structure
      if (workflowDetails && workflowDetails.steps) {
        workflowDetails.steps.forEach(step => {
          const stepId = step.id;
          
          // Initialize step field structure
          workflowStepFields[stepId] = {
            fields: {},
            staticFields: {}
          };
          
          // Map dynamic service fields to this step's fields object
          Object.entries(processedCustomerFields).forEach(([fieldKey, fieldValue]) => {
            // Check if this is a dynamic service field (UUID pattern or service field)
            const isDynamicField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(fieldKey) ||
                                   fieldKey.startsWith('field_') ||
                                   !['firstNameEn', 'lastNameEn', 'passportCopy', 'visaPage', 'emiratesId', 'birthCertificate'].includes(fieldKey);
            
            if (isDynamicField) {
              debugLog(`Mapping dynamic field ${fieldKey} to step ${stepId} fields`);
              workflowStepFields[stepId].fields[fieldKey] = fieldValue;
            } else {
              debugLog(`Mapping static field ${fieldKey} to step ${stepId} staticFields`);
              workflowStepFields[stepId].staticFields[fieldKey] = fieldValue;
            }
          });
          
          debugLog(`Step ${stepId} field mapping:`, {
            dynamicFields: Object.keys(workflowStepFields[stepId].fields),
            staticFields: Object.keys(workflowStepFields[stepId].staticFields)
          });
        });
      }
      
      // Prepare the request data with proper workflow step structure
      const isOrgWorkflow = selectedTargetType === 'ORGANIZATION';

      const requestData = {
        workflowId: workflowDetails.id,
        customerOrganisationId: null,
        requestTitle: formData.requestTitle,
        description: formData.description,
        customerUid: isOrgWorkflow ? '' : (formData.customerUid || (selectedEmployee ? selectedEmployee.uid : '')),
        stepFields: workflowStepFields,
        priority: formData.priority,
        hasFileUploads: false,
        stepAssignments: stepAssignments
      };

      // Set customerOrganisationId
      if (customerUidValidation.customerInfo?.id && customerUidValidation.customerInfo.id.trim() !== '') {
        requestData.customerOrganisationId = customerUidValidation.customerInfo.id;
      } else if (organizationId) {
        requestData.customerOrganisationId = organizationId;
      } else if (!isOrgWorkflow && selectedEmployee && selectedEmployee.organizationId) {
        requestData.customerOrganisationId = selectedEmployee.organizationId;
      }

      if (!requestData.customerOrganisationId) {
        dispatch(dismissLoader());
        dispatch(showErroMessage('Organization ID is required. Please select a valid customer or employee with an organization.'));
        console.error('Cannot submit workflow request: Missing organization ID');
        return;
      }

      // Include employee assignment data only for EMPLOYEE workflows
      if (!isOrgWorkflow && selectedEmployee) {
        requestData.assignedEmployeeUid = selectedEmployee.uid;
        requestData.assignedEmployeeId = selectedEmployee.employeeId || selectedEmployee.id;
        requestData.employeeName = selectedEmployee.nameEn ||
                                 `${selectedEmployee.firstNameEn || ''} ${selectedEmployee.lastNameEn || ''}` ||
                                 'Unknown';
        requestData.employeeUid = selectedEmployee.uid;
        requestData.employeeId = selectedEmployee.employeeId || selectedEmployee.id;
      }
      
      debugLog('Creating workflow request with data:', requestData);
      
      // Sanitize: remove phone/mobile fields if not present in current workflow or invalid
      try {
        const renderedFieldNames = new Set([
          ...(workflowFields?.customerInfoFields || []).map(f => f?.name || f?.code).filter(Boolean),
          ...(workflowFields?.formFields || []).map(f => f?.name || f?.code).filter(Boolean),
        ].map(s => s.toString().toLowerCase()));

        const isPhoneKey = (k) => {
          const kl = (k || '').toString().toLowerCase();
          return kl.includes('phone') || kl.includes('mobile');
        };
        const isValidPhone = (v) => {
          if (!v || typeof v !== 'string') return false;
          const digits = (v.match(/\d/g) || []).length;
          if (digits < 7 || digits > 15) return false;
          return /^\+?[0-9\s\-\(\)]{7,20}$/.test(v);
        };

        // Clean inside stepFields
        if (requestData?.stepFields && typeof requestData.stepFields === 'object') {
          Object.values(requestData.stepFields).forEach(step => {
            if (!step || typeof step !== 'object') return;
            ['fields', 'staticFields'].forEach(bucket => {
              if (!step[bucket] || typeof step[bucket] !== 'object') return;
              Object.keys(step[bucket]).forEach(key => {
                const val = step[bucket][key];
                const keyLc = key.toLowerCase();
                const inRendered = renderedFieldNames.has(keyLc);
                if (isPhoneKey(key) && (!inRendered || !isValidPhone(val))) {
                  delete step[bucket][key];
                }
              });
            });
          });
        }
      } catch (e) {
        console.warn('Phone/mobile sanitizer skipped due to error:', e?.message || e);
      }

      // Make API call to create workflow request
      const response = await AppService.post('api/workflow-requests', requestData);
      
      debugLog('Workflow request created:', response);
      
      
      // Store the response data
      setResponse(response);
      // Backend returns TWorkflowRequest with orgSequenceNumber and formattedRequestId, not requestNumber
      const reqNumber = response?.orgSequenceNumber || response?.formattedRequestId || response?.requestNumber || null;
      setRequestNumber(reqNumber);
      // Controller currently returns the TWorkflowRequest (no instanceId). Use workflowInstanceId if backend provides it, or nested instance id in future shapes
      const instId = response?.workflowInstanceId || response?.instanceId || response?.instance?.id || null;
      setInstanceId(instId);
      setSuccess(true);
      setSubmitting(false);
      
      // Show success message using the correct field
      dispatch(showSuccessMessage(`Workflow request created successfully with request number ${reqNumber || 'N/A'}`));
      
      // Scroll to top to show success message
      window.scrollTo(0, 0);
      
      // Navigate to requests page after 3 seconds
      setTimeout(() => {
        navigate('/CST-Workflow/requests');
      }, 3000);
    } catch (error) {
      console.error('Error creating workflow request:', error);
      setSuccess(false);
      setError({
        message: 'Failed to create workflow request',
        details: error.message
      });
    } finally {
      setSubmitting(false);
      dispatch(dismissLoader());
    }
  };

  const handleBack = () => {
    navigate('/CST-Workflow/requests');
  };
  
  
  // Open assignment dialog for a step
  const handleOpenAssignmentDialog = (step) => {
    debugLog('Opening Assign Workflow Step:', step.name);
    setCurrentStepForAssignment(step);
    setAssignmentDialogOpen(true);
  };
  
  // Close assignment dialog
  const handleCloseAssignDialog = () => {
    setAssignmentDialogOpen(false);
    setCurrentStepForAssignment(null);
  };
  
  // Handle assignment completion
  const handleAssignmentComplete = (assignmentData) => {
    debugLog('Assignment completed for workflow step:', currentStepForAssignment?.name);
    debugLog('Assignment data:', assignmentData);
    
    if (!currentStepForAssignment) return;
    
    // Update assigned users state
    if (assignmentData.userId) {
      setAssignedUsers(prev => ({
        ...prev,
        [currentStepForAssignment.id]: {
          id: assignmentData.userId,
          name: assignmentData.userName || 'Assigned User'
        }
      }));
    }
    
    // Close the dialog
    handleCloseAssignDialog();
  };
  
  // Method to calculate service costs based on current assignment state
  const calculateServiceCosts = () => {
    debugLog('Calculating service costs with current assignment state');
    
    if (!workflowDetails) {
      debugLog('No workflow details available');
      return {
        totalServiceCharge: 0,
        totalGovtCharge: 0,
        totalTax: 0,
        totalCost: 0,
        services: []
      };
    }
    
    // Extract steps from the parsed workflow definition if needed
    let steps = [];
    if (workflowDetails.steps && Array.isArray(workflowDetails.steps)) {
      steps = workflowDetails.steps;
    } else if (workflowDetails.definition) {
      try {
        // Try to parse the definition if it's a string
        const definitionObj = typeof workflowDetails.definition === 'string' 
          ? JSON.parse(workflowDetails.definition) 
          : workflowDetails.definition;
          
        steps = definitionObj.steps || [];
      } catch (e) {
        console.error('Error parsing workflow definition:', e);
      }
    }
    
    if (!steps || !Array.isArray(steps) || steps.length === 0) {
      debugLog('No valid steps found in workflow details');
      return {
        totalServiceCharge: 0,
        totalGovtCharge: 0,
        totalTax: 0,
        totalCost: 0,
        services: []
      };
    }
    
    // Process each step from the workflow
    const services = steps.map(step => {
    // Check if this step is assigned to organization
    const stepAssignmentType = stepAssignmentTypes[step.id] || 'thaklis';
    const isOrgAssignment = stepAssignmentType === 'organization';
    
    debugLog(`Step ${step.id} (${step.name || step.serviceName || 'Unnamed'}) assignment: ${stepAssignmentType}, isOrgAssignment=${isOrgAssignment}`);
    
    // Set base values for government fee, service charge and tax
    // Use random values for demo/testing purposes
    const govtFee = 25 + Math.floor(Math.random() * 15); // Government fee is always charged
    
    // If assigned to organization, zero out service charge and tax
    const serviceCharge = isOrgAssignment ? 0 : (40 + Math.floor(Math.random() * 20));
    const tax = isOrgAssignment ? 0 : (serviceCharge * 0.05);
    
    return {
      id: step.id,
      name: step.name || 'Unnamed Step',
      serviceName: step.serviceName || 'Service',
      govtCharge: govtFee,
      serviceCharge: serviceCharge,
      taxAmount: tax,
      totalCost: govtFee + serviceCharge + tax,
      isOrgAssignment: isOrgAssignment
    };
  });
  
  // Calculate totals
  const totals = services.reduce(
    (acc, service) => {
      return {
        totalServiceCharge: acc.totalServiceCharge + service.serviceCharge,
        totalGovtCharge: acc.totalGovtCharge + service.govtCharge,
        totalTax: acc.totalTax + service.taxAmount
      };
    }, 
    { totalServiceCharge: 0, totalGovtCharge: 0, totalTax: 0 }
  );
  
  const result = {
    ...totals,
    totalCost: totals.totalServiceCharge + totals.totalGovtCharge + totals.totalTax,
    services
  };
  
    debugLog('Calculated service costs:', result);
    return result;
  };

  // Handler for showing cost dialog
  const handleShowCostDialog = () => {
    debugLog('Show cost dialog button clicked');
    // Calculate the latest costs based on current assignments
    const updatedCosts = calculateServiceCosts();
    setServiceCosts(updatedCosts);
    debugLog('Updated costs for dialog:', updatedCosts);
    
    // Force the dialog to open by setting servicesEnabled to true first
    setServicesEnabled(true);
    setShowCostDialog(true);
    
    debugLog('Dialog state after clicking button:', { showCostDialog: true, servicesEnabled: true });
  };
  
  // Handler for closing cost dialog
  const handleCloseCostDialog = () => {
    setShowCostDialog(false);
  };
  
  // Utility function to determine if a file is a PDF
  const isPdfFile = (fileName, contentType) => {
    if (contentType && contentType.toLowerCase().includes('pdf')) return true;
    if (fileName && fileName.toLowerCase().endsWith('.pdf')) return true;
    return false;
  };
  
  // Utility function to determine file type from name and content type
  const determineFileType = (fileName, contentType) => {
    // Check if we have a valid content type
    if (contentType && contentType !== 'application/octet-stream') {
      // Use the content type as the primary indicator
      if (contentType.startsWith('image/')) return 'image';
      if (contentType.toLowerCase().includes('pdf')) return 'pdf';
    }
    
    // Fallback to file extension
    if (fileName) {
      const lowerFileName = fileName.toLowerCase();
      if (lowerFileName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) return 'image';
      if (lowerFileName.endsWith('.pdf')) return 'pdf';
    }
    
    // Default to 'other' if we can't determine the type
    return 'other';
  };
  
  // Handle document preview
  const handleDocumentPreview = (document) => {
    try {
      debugLog('Document preview requested:', document);
      
      // Set dialog to loading state
      setFileViewerDialog({
        open: true,
        fileName: document.name || 'Document',
        fileUrl: '',
        fileType: '',
        loading: true,
        error: null
      });
      
      // If document is a new file (has file object)
      if (document.file && document.file instanceof File) {
        debugLog('Handling file object preview:', document.file.name, document.file.type);
        const objectUrl = URL.createObjectURL(document.file);
        const fileType = determineFileType(document.file.name, document.file.type);
        
        setFileViewerDialog(prev => ({
          ...prev,
          fileUrl: objectUrl,
          fileType: document.file.type,
          loading: false
        }));
        return;
      }
      
      // If document has a direct URL
      if (document.documentUrl) {
        debugLog('Handling document URL preview:', document.documentUrl);
        // Determine if this is a full URL or a relative path
        let fullUrl;
        if (document.documentUrl.startsWith('http')) {
          fullUrl = document.documentUrl;
        } else if (document.documentUrl.startsWith('/v1/')) {
          fullUrl = `${process.env.REACT_APP_API_URL}${document.documentUrl}`;
        } else if (document.documentUrl.startsWith('/api/')) {
          fullUrl = `${process.env.REACT_APP_API_URL}${document.documentUrl}`;
        } else if (document.documentUrl.startsWith('/')) {
          fullUrl = `${process.env.REACT_APP_API_URL}/api${document.documentUrl}`;
        } else {
          fullUrl = `${process.env.REACT_APP_API_URL}/api/${document.documentUrl}`;
        }
        
        debugLog('Full URL for fetch:', fullUrl);
        
        // Get auth token
        const token = getAuthToken();
        if (!token) {
          dispatch(showErroMessage('Authentication token not found'));
          setFileViewerDialog(prev => ({
            ...prev,
            loading: false,
            error: 'Authentication token not found'
          }));
          return;
        }
        
        // Create a fetch request with auth header
        fetch(fullUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then(response => {
          if (!response.ok) {
            console.error('HTTP error response:', response.status, response.statusText);
            return response.text().then(text => {
              try {
                const errorData = JSON.parse(text);
                throw new Error(`HTTP error ${response.status}: ${errorData.message || response.statusText}`);
              } catch (e) {
                throw new Error(`HTTP error ${response.status}: ${response.statusText}\nResponse: ${text}`);
              }
            });
          }
          return response.blob();
        })
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);
          const contentType = blob.type;
          debugLog('Document loaded successfully, content type:', contentType);
          
          // Use our utility to determine the file type
          const fileType = determineFileType(document.name, contentType);
          debugLog('Determined file type:', fileType);
          
          setFileViewerDialog(prev => ({
            ...prev,
            fileUrl: objectUrl,
            fileType: contentType,
            loading: false
          }));
        })
        .catch(error => {
          console.error('Error viewing document:', error);
          dispatch(showErroMessage(`Error viewing document: ${error.message}`));
          setFileViewerDialog(prev => ({
            ...prev,
            loading: false,
            error: error.message
          }));
        });
        return;
      }
      
      // If document is an existing document (has uid and documentType)
      if (document.uid && document.documentType) {
        debugLog('Handling document by UID and type:', document.uid, document.documentType);
        
        // Check if this is a dynamic service field (UUID pattern)
        const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(document.documentType);
        
        if (isDynamicServiceField) {
          debugLog('Dynamic service field detected in NewWorkflowRequest, cannot use employee document API for UUID documentType:', document.documentType);
          debugLog('Dynamic service fields should use file path URLs instead');
          dispatch(dismissLoader());
          return; // Exit early for dynamic service fields
        }
        
        dispatch(showLoader());
        
        // Construct the API URL for static document fields only
        const apiUrl = `/v1/users/${document.uid}/documents/${document.documentType}/download`;
        const fullUrl = `${process.env.REACT_APP_API_URL}${apiUrl}`;
        debugLog('Full URL for fetch:', fullUrl);
        
        // Get auth token
        const token = getAuthToken();
        if (!token) {
          dispatch(dismissLoader());
          dispatch(showErroMessage('Authentication token not found'));
          setFileViewerDialog(prev => ({
            ...prev,
            loading: false,
            error: 'Authentication token not found'
          }));
          return;
        }
        
        // Create a fetch request with auth header
        fetch(fullUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then(response => {
          dispatch(dismissLoader());
          if (!response.ok) {
            console.error('HTTP error response:', response.status, response.statusText);
            return response.text().then(text => {
              try {
                const errorData = JSON.parse(text);
                throw new Error(`HTTP error ${response.status}: ${errorData.message || response.statusText}`);
              } catch (e) {
                throw new Error(`HTTP error ${response.status}: ${response.statusText}\nResponse: ${text}`);
              }
            });
          }
          return response.blob();
        })
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);
          const contentType = blob.type;
          debugLog('Document loaded successfully, content type:', contentType, 'size:', blob.size);
          
          // Use our utility to determine the file type
          const fileType = determineFileType(document.documentType, contentType);
          debugLog('Determined file type:', fileType);
          
          setFileViewerDialog(prev => ({
            ...prev,
            fileUrl: objectUrl,
            fileType: contentType,
            loading: false
          }));
        })
        .catch(error => {
          console.error('Error viewing document:', error);
          dispatch(showErroMessage(`Error viewing document: ${error.message}`));
          setFileViewerDialog(prev => ({
            ...prev,
            loading: false,
            error: error.message
          }));
        });
        return;
      }
      
      // If we get here, we don't have enough information to fetch the document
      console.error('Invalid document reference:', document);
      dispatch(showErroMessage('Invalid document reference'));
      setFileViewerDialog(prev => ({
        ...prev,
        loading: false,
        error: 'Invalid document reference'
      }));
    } catch (error) {
      console.error('Error in handleDocumentPreview:', error);
      dispatch(showErroMessage(`Error previewing document: ${error.message}`));
      setFileViewerDialog(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  // Handle closing the file viewer dialog
  const handleCloseFileViewer = () => {
    // Clean up any object URLs to prevent memory leaks
    if (fileViewerDialog.fileUrl && fileViewerDialog.fileUrl.startsWith('blob:')) {
      URL.revokeObjectURL(fileViewerDialog.fileUrl);
    }
    
    // Reset the dialog state
    setFileViewerDialog({
      open: false,
      fileName: '',
      fileUrl: '',
      fileType: '',
      loading: false,
      error: null
    });
  };
  
  // Expose handleDocumentPreview to global scope for WorkflowFileField component
  useEffect(() => {
    // Make handleDocumentPreview available globally
    window.handleDocumentPreview = handleDocumentPreview;
    
    // Cleanup function
    return () => {
      delete window.handleDocumentPreview;
    };
  }, []);

  // Start fresh on mount and clean up on unmount. The autofill writes shared globals
  // (window.currentFormData / selectedEmployee / employee uid) that otherwise persist across
  // navigation, so re-opening this page shows the previous request's auto-filled data until a full
  // page refresh. Resetting them here makes each new request begin empty.
  useEffect(() => {
    window.currentFormData = { requestTitle: '', description: '', priority: 'MEDIUM', additionalInfo: {}, customerUid: '' };
    delete window.selectedEmployee;
    delete window.currentEmployeeUid;
    delete window.currentEmployeeId;
    return () => {
      delete window.currentFormData;
      delete window.selectedEmployee;
      delete window.currentEmployeeUid;
      delete window.currentEmployeeId;
    };
  }, []);

  // Handle document download
  const handleDocumentDownload = async (document) => {
    try {
      // For new documents with file objects, create a temporary URL and trigger download
      if (document.isNew && document.file) {
        const objectUrl = URL.createObjectURL(document.file);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = document.name || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL after download
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        return;
      }
      
      // For existing documents, use the API endpoint
      if (document.id && !document.isNew) {
        const apiBase = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:8080`;
        const url = `${apiBase}/v1/workflow-requests/${formData.id}/documents/${document.id}/download`;
        const token = localStorage.getItem('jwt_token') || (localStorage.getItem('authInfo') ? JSON.parse(localStorage.getItem('authInfo')).token : null) || localStorage.getItem('token') || localStorage.getItem('authToken');
        if (!token) {
          throw new Error('Missing auth token');
        }
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) {
          throw new Error(`Download failed: ${res.status} ${res.statusText}`);
        }
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = document.name || 'document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      dispatch(showErroMessage('Unable to download document. Please try again.'));
    }
  };


  
  // Handle document field auto-filling from employee documents
  const handleDocumentFieldAutoFill = async (fieldName, employee, updatedFormData) => {
    console.log(`Attempting to auto-fill document field: ${fieldName} for employee:`, {
      employeeId: employee.id || employee.employeeId,
      employeeUid: employee.uid,
      timestamp: new Date().toISOString()
    });
    
    // First check if employee has documents in the employee object
    const hasLocalDocuments = employee.documents && employee.documents.length > 0;
    
    if (hasLocalDocuments) {
      console.log('Found documents in employee data:', employee.documents.length);
    }
    
    let matchingDoc = null;
    
    // Check if employee has documents array
    if (employee.documents && Array.isArray(employee.documents)) {
      console.log(`Employee has ${employee.documents.length} documents, checking for ${fieldName}`);
      
      // Try to find a document that matches this field name
      matchingDoc = employee.documents.find(doc => 
        doc.documentType && doc.documentType.toLowerCase() === fieldName.toLowerCase()
      );
    }
    
    // If no matching document found in the documents array, try to fetch it
    if (!matchingDoc) {
      try {
        console.log(`No matching document found in employee data, trying to fetch from API for ${fieldName}`);
        
        // Get auth token from local storage
        const token = localStorage.getItem('token') || 
                    localStorage.getItem('jwt_token') || 
                    localStorage.getItem('authToken');
        
        if (!token) {
          console.error('Authentication token not found');
          return false;
        }
        
        // Get the base URL from environment or current origin
        const apiBaseUrl = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:8080`;
        
        // Try to fetch documents for this employee
        const response = await fetch(`${apiBaseUrl}/v1/users/${employee.uid}/documents`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const documents = await response.json();
          console.log(`Fetched ${documents.length} documents for employee ${employee.uid}`);
          
          // Try to find a document that matches this field name
          matchingDoc = documents.find(doc => 
            doc.documentType && doc.documentType.toLowerCase() === fieldName.toLowerCase()
          );
        } else {
          console.error(`Failed to fetch documents: ${response.status}`);
        }
      } catch (error) {
        console.error('Error fetching employee documents:', error);
      }
    }
    
    if (matchingDoc) {
      console.log(`Found matching document for ${fieldName}:`, matchingDoc);
      
      // Check if this is a dynamic service field (UUID pattern)
      const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(matchingDoc.documentType);
      
      // Create a document reference object with necessary metadata
      const documentReference = {
        isReference: true,                // Flag to indicate this is a reference to an existing document
        id: matchingDoc.id || matchingDoc.filePath, // Document ID for backend association
        uid: matchingDoc.uid || employee.uid,
        documentType: matchingDoc.documentType, // Keep the original document type from the backend
        fileName: matchingDoc.fileName || `${fieldName} Document`,
        contentType: matchingDoc.contentType || 'application/pdf',
        fileSize: matchingDoc.fileSize || 0,
        // Source employee UID - critical for document viewing
        employeeUid: employee.uid,
        filePath: matchingDoc.filePath || matchingDoc.id,
        // Add document URL for tracking - but only for static fields
        documentUrl: isDynamicServiceField ? 
          (matchingDoc.filePath ? `/v1/files/download?filePath=${encodeURIComponent(matchingDoc.filePath)}` : null) :
          `/v1/users/${employee.uid}/documents/${matchingDoc.documentType}/download`
      };
      
      // Ensure additionalInfo exists
      if (!updatedFormData.additionalInfo) {
        updatedFormData.additionalInfo = {};
      }
      
      // Set the field value in the form data
      updatedFormData.additionalInfo[fieldName] = documentReference;
      
      console.log(`Auto-filled document field ${fieldName} with document reference:`, documentReference);
      return true; // Document was auto-filled
    } else {
      console.log(`No matching document found for field ${fieldName}`);
      return false; // No matching document found
    }
  };

  // Fetch employee's organization ID
  const fetchEmployeeOrganizationId = async (employeeUid) => {
    try {
      console.log(`Fetching organization ID for employee: ${employeeUid}`);
      const response = await AppService.get(`/api/employees/${employeeUid}`);
      console.log('Employee details fetched:', response);
      
      if (response && response.organizationId) {
        console.log(`Setting organization ID from employee: ${response.organizationId}`);
        setOrganizationId(response.organizationId);
        return response.organizationId;
      }
    } catch (error) {
      console.error('Error fetching employee organization ID:', error);
    }
    return null;
  };

  const handleEmployeeAdded = (newEmployee) => {
    console.log('New employee created:', newEmployee);

    setOpenQuickAddEmployee(false);

    if(newEmployee){
      handleEmployeeSelect(newEmployee);
    }
  }

  // Handle employee selection
  const handleEmployeeSelect = async (employee) => {
    console.log('handleEmployeeSelect called with employee:', employee);
    
    if (!employee) {
      console.log('No employee provided, clearing employee-related fields');
      // Clear employee-related fields
      const updatedFormData = JSON.parse(JSON.stringify(formData));
      updatedFormData.assignedEmployeeUid = '';
      updatedFormData.assignedEmployeeId = '';
      updatedFormData.employeeName = '';
      if (updatedFormData.additionalInfo) {
        const employeeRelatedPatterns = ['employee', 'name', 'email', 'mobile', 'phone', 'department', 'position', 'title', 'job', 'designation', 'id', 'uid'];
        const fieldsToReset = Object.keys(updatedFormData.additionalInfo).filter(key => {
          const lowerKey = key.toLowerCase();
          return employeeRelatedPatterns.some(pattern => lowerKey.includes(pattern));
        });
        console.log('Fields to reset:', fieldsToReset);
        fieldsToReset.forEach(field => {
          updatedFormData.additionalInfo[field] = '';
        });
      }
      setFormData(updatedFormData);
      setSelectedEmployee(null);
      setOrganizationId(null);
      setTimeout(() => {
        setFormData(currentFormData => JSON.parse(JSON.stringify(currentFormData)));
      }, 100);
      return;
    }
    
    console.log('Setting selected employee and preparing to map data');
    setSelectedEmployee(employee);
    
    // Fetch employee's organization ID if available
    if (employee.uid) {
      fetchEmployeeOrganizationId(employee.uid);
    } else if (employee.organizationId) {
      // If the employee object already has the organizationId, use it directly
      console.log(`Using organization ID directly from employee object: ${employee.organizationId}`);
      setOrganizationId(employee.organizationId);
    }
    
    const updatedFormData = JSON.parse(JSON.stringify(formData));
    
    // Extract and normalize employee data
    const employeeUid = employee.uid || '';
    const employeeId = employee.employeeId || employee.id || '';
    let firstName = employee.firstNameEn || employee.firstName || '';
    let middleName = employee.middleNameEn || employee.middleName || '';
    let lastName = employee.lastNameEn || employee.lastName || '';
    let fullName = employee.nameEn || employee.fullName || '';
    
    // Handle name parsing and construction
    if (!fullName && (firstName || lastName)) {
      fullName = [firstName, middleName, lastName].filter(Boolean).join(' ').trim();
      console.log(`Constructed full name from parts: ${fullName}`);
    } else if (fullName && (!firstName || !lastName)) {
      const nameParts = fullName.split(' ');
      if (nameParts.length > 1) {
        if (!firstName) firstName = nameParts[0];
        if (!lastName) lastName = nameParts.slice(1).join(' ');
        console.log(`Parsed name parts from full name: firstName=${firstName}, lastName=${lastName}`);
      }
    }
    
    const employeeName = fullName;
    
    // Set primary employee identifiers in form data
    updatedFormData.assignedEmployeeUid = employeeUid;
    updatedFormData.assignedEmployeeId = employeeId;
    updatedFormData.employeeName = employeeName;
    if (!updatedFormData.additionalInfo) updatedFormData.additionalInfo = {};
    
    console.log(`Primary employee identifiers set: uid=${employeeUid}, id=${employeeId}, name=${employeeName}`);
    
    // Set global variable for employee UID to ensure it's available for file uploads
    window.currentEmployeeUid = employeeUid;
    console.log(`%cSetting global employeeUid for file uploads:`, 'color: blue; font-weight: bold', employeeUid);
    
    // Create comprehensive employee data mapping object with all possible field name variations
    const employeeData = {
      // Name fields
      firstName, first_name: firstName, firstname: firstName, first: firstName, employeeFirstName: firstName, employee_first_name: firstName,
      middleName, middle_name: middleName, middlename: middleName, middle: middleName, employeeMiddleName: middleName, employee_middle_name: middleName,
      lastName, last_name: lastName, lastname: lastName, last: lastName, employeeLastName: lastName, employee_last_name: lastName,
      fullName: employeeName, full_name: employeeName, fullname: employeeName, name: employeeName, employeeName: employeeName, employee_name: employeeName,
      nameEn: employeeName, name_en: employeeName, 
      
      // Contact fields
      email: employee.email || '', emailAddress: employee.email || '', email_address: employee.email || '', employeeEmail: employee.email || '', employee_email: employee.email || '',
      phone: employee.phone || employee.mobileNumber || employee.phoneNumber || '', phoneNumber: employee.phone || employee.mobileNumber || employee.phoneNumber || '', phone_number: employee.phone || employee.mobileNumber || employee.phoneNumber || '',
      mobile: employee.mobileNumber || employee.mobile || employee.phone || '', mobileNumber: employee.mobileNumber || employee.mobile || employee.phone || '', mobile_number: employee.mobileNumber || employee.mobile || employee.phone || '',
      employeePhone: employee.phone || employee.mobileNumber || employee.phoneNumber || '', employee_phone: employee.phone || employee.mobileNumber || employee.phoneNumber || '', employeeMobile: employee.mobileNumber || employee.mobile || employee.phone || '', employee_mobile: employee.mobileNumber || employee.mobile || employee.phone || '',
      
      // Job fields
      department: employee.department?.departmentName || '', 
      departmentName: employee.department?.departmentName || '', 
      department_name: employee.department?.departmentName || '', 
      employeeDepartment: employee.department?.departmentName || '', 
      employee_department: employee.department?.departmentName || '',
      position: employee.position?.positionName || employee.jobTitle || employee.designation || '', jobTitle: employee.jobTitle || employee.position?.positionName || employee.designation || '',
      job_title: employee.jobTitle || employee.position?.positionName || employee.designation || '', employeePosition: employee.position?.positionName || employee.jobTitle || employee.designation || '', employee_position: employee.position?.positionName || employee.jobTitle || employee.designation || '',
      designation: employee.designation || employee.jobTitle || employee.position?.positionName || '', employeeDesignation: employee.designation || employee.jobTitle || employee.position?.positionName || '', employee_designation: employee.designation || employee.jobTitle || employee.position?.positionName || '',
      
      // ID fields
      employeeId: employeeId, employee_id: employeeId, id: employeeId,
      employeeUid: employeeUid, employee_uid: employeeUid, uid: employeeUid,
      
      // Other personal info
      gender: employee.gender || '', sex: employee.gender || '',
      area: employee.area || '',
      nationality: employee.nationality || '',
      dateOfBirth: employee.dateOfBirth || employee.birthDate || '',
      birthDate: employee.dateOfBirth || employee.birthDate || '',
    };

    // 1. Direct mapping of static fields from employee object
    console.log('Step 1: Direct mapping of static fields from employee object');
    const staticFieldCodes = [
      'firstNameEn', 'middleNameEn', 'lastNameEn', 'nameEn', 
      'gender', 'area', 'nationality', 'dateOfBirth',
      'email', 'mobileNumber', 'phone', 'department', 'position', 'jobTitle'
    ];
    
    staticFieldCodes.forEach(code => {
      if (employee[code] !== undefined && employee[code] !== null) {
        console.log(`Found static field match for ${code}:`, employee[code]);
        updatedFormData.additionalInfo[code] = employee[code];
      }
    });

    // 2. Map employee data to form fields based on field metadata
    if (workflowFields && workflowFields.allFields) {
      console.log('Step 2: Mapping employee data to workflow fields based on field metadata');
      console.log(`Total workflow fields to check: ${workflowFields.allFields.length}`);
      
      workflowFields.allFields.forEach(field => {
        const fieldName = field.name || field.code || '';
        if (!fieldName) {
          console.log('Skipping field with no name or code');
          return;
        }

        // Skip if field is already populated by direct static field mapping
        if (updatedFormData.additionalInfo[fieldName] && staticFieldCodes.includes(fieldName)) {
          console.log(`Field ${fieldName} already populated by static field mapping, skipping pattern matching`);
          return;
        }

        // Check if field name matches any of the employee data keys directly
        if (employeeData[fieldName] !== undefined && employeeData[fieldName] !== null && employeeData[fieldName] !== '') {
          console.log(`Direct match found for field ${fieldName}:`, employeeData[fieldName]);
          updatedFormData.additionalInfo[fieldName] = employeeData[fieldName];
          return;
        }

        // Pattern-based matching for fields with employee-related categories
        const fieldCategory = (field.category || '').toUpperCase();
        const isEmployeeRelatedCategory = [
          'PERSONAL_INFO', 'CONTACT_INFO', 'EMPLOYEE_INFO', 'EMPLOYEE', 'PERSONAL', 'CONTACT'
        ].includes(fieldCategory);
        
        if (isEmployeeRelatedCategory) {
          const lowerFieldName = fieldName.toLowerCase();
          let matched = false;
          
          // Check for name-related fields
          if (lowerFieldName.includes('name')) {
            if (lowerFieldName.includes('first')) {
              console.log(`Category-based match for first name field ${fieldName}:`, firstName);
              updatedFormData.additionalInfo[fieldName] = firstName;
              matched = true;
            } else if (lowerFieldName.includes('middle')) {
              console.log(`Category-based match for middle name field ${fieldName}:`, middleName);
              updatedFormData.additionalInfo[fieldName] = middleName;
              matched = true;
            } else if (lowerFieldName.includes('last')) {
              console.log(`Category-based match for last name field ${fieldName}:`, lastName);
              updatedFormData.additionalInfo[fieldName] = lastName;
              matched = true;
            } else if (lowerFieldName.includes('full') || lowerFieldName === 'name') {
              console.log(`Category-based match for full name field ${fieldName}:`, fullName);
              updatedFormData.additionalInfo[fieldName] = fullName;
              matched = true;
            }
          }
          
          // Check for contact-related fields
          else if (lowerFieldName.includes('email')) {
            console.log(`Category-based match for email field ${fieldName}:`, employee.email || '');
            updatedFormData.additionalInfo[fieldName] = employee.email || '';
            matched = true;
          } else if (lowerFieldName.includes('phone') || lowerFieldName.includes('mobile')) {
            debugLog(`Category-based match for phone field ${fieldName}:`, employee.mobileNumber || employee.phone || '');
            updatedFormData.additionalInfo[fieldName] = employee.mobileNumber || employee.phone || '';
            matched = true;
          }
          
          // Check for job-related fields
          else if (lowerFieldName.includes('department')) {
            const departmentValue = employee.department?.departmentName || '';
            debugLog(`Category-based match for department field ${fieldName}:`, departmentValue);
            updatedFormData.additionalInfo[fieldName] = departmentValue;
            matched = true;
          } else if (lowerFieldName.includes('position') || lowerFieldName.includes('job') || lowerFieldName.includes('title') || lowerFieldName.includes('designation')) {
            const positionValue = employee.position?.positionName || '';
            debugLog(`Category-based match for position field ${fieldName}:`, positionValue);
            updatedFormData.additionalInfo[fieldName] = positionValue;
            matched = true;
          }
          
          // Check for ID-related fields
          else if (lowerFieldName.includes('id') && !lowerFieldName.includes('uid')) {
            // Avoid populating document-related ID fields (e.g., emiratesId, passportNumber, visaNumber) with employeeId
            const isDocumentIdField = (
              lowerFieldName.includes('emirates') ||
              lowerFieldName.includes('eid') ||
              lowerFieldName.includes('passport') ||
              lowerFieldName.includes('visa') ||
              lowerFieldName.includes('card') ||
              lowerFieldName.includes('document')
            );
            // Only populate employeeId into fields that clearly refer to employee identity
            const isEmployeeIdField = (
              (lowerFieldName.includes('employee') && lowerFieldName.includes('id')) ||
              ['employeeid','employee_id','empid','emp_id','staffid','staff_id'].includes(lowerFieldName)
            );
            if (isEmployeeIdField && !isDocumentIdField) {
              debugLog(`Category-based match for EMPLOYEE ID field ${fieldName}:`, employeeId);
              updatedFormData.additionalInfo[fieldName] = employeeId;
              matched = true;
            }
          } else if (lowerFieldName.includes('uid')) {
            debugLog(`Category-based match for UID field ${fieldName}:`, employeeUid);
            updatedFormData.additionalInfo[fieldName] = employeeUid;
            matched = true;
          }
          
          // Check for other personal info fields
          else if (lowerFieldName.includes('gender') || lowerFieldName.includes('sex')) {
            debugLog(`Category-based match for gender field ${fieldName}:`, employee.gender || '');
            updatedFormData.additionalInfo[fieldName] = employee.gender || '';
            matched = true;
          } else if (lowerFieldName.includes('area')) {
            console.log(`Category-based match for area field ${fieldName}:`, employee.area || '');
            updatedFormData.additionalInfo[fieldName] = employee.area || '';
            matched = true;
          } else if (lowerFieldName.includes('nationality')) {
            console.log(`Category-based match for nationality field ${fieldName}:`, employee.nationality || '');
            updatedFormData.additionalInfo[fieldName] = employee.nationality || '';
            matched = true;
          } else if (lowerFieldName.includes('birth') || lowerFieldName.includes('dob')) {
            console.log(`Category-based match for birth date field ${fieldName}:`, employee.dateOfBirth || employee.birthDate || '');
            updatedFormData.additionalInfo[fieldName] = employee.dateOfBirth || employee.birthDate || '';
            matched = true;
          }
          
          if (matched) {
            console.log(`Successfully mapped field ${fieldName} based on category ${fieldCategory}`);
          } else {
            console.log(`No pattern match found for field ${fieldName} in category ${fieldCategory}`);
          }
        }
      });
    }

    // 2B. Apply utility-based employee mapping (non-destructive)
    try {
      const allWorkflowFields = workflowFields?.allFields || [];
      const mappedFromEmployee = mapEmployeeDataToWorkflowFields(employee, allWorkflowFields);
      console.log('Step 2B: Employee utility mapped fields:', mappedFromEmployee);

      Object.entries(mappedFromEmployee).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') return;
        const existing = updatedFormData.additionalInfo[key];
        if (existing === null || existing === undefined || existing === '') {
          updatedFormData.additionalInfo[key] = value;
        }
      });
    } catch (e) {
      console.error('Error during employee utility mapping (Step 2B):', e);
    }

    // 2C. Synchronize values between field.name and field.code (non-destructive)
    // Some UI components bind by field.code while others bind by field.name.
    // Ensure that if one key has a value and the other is empty, we mirror the value.
    try {
      const allFields = workflowFields?.allFields || [];
      allFields.forEach((field) => {
        const nameKey = field?.name;
        const codeKey = field?.code;
        if (!nameKey && !codeKey) return;

        const hasName = nameKey !== undefined && nameKey !== null && nameKey !== '';
        const hasCode = codeKey !== undefined && codeKey !== null && codeKey !== '';

        if (!hasName || !hasCode || nameKey === codeKey) return;

        const nameVal = updatedFormData?.additionalInfo?.[nameKey];
        const codeVal = updatedFormData?.additionalInfo?.[codeKey];

        const isEmpty = (v) => v === undefined || v === null || v === '';

        // If code is empty but name has value, copy to code
        if (!isEmpty(nameVal) && isEmpty(codeVal)) {
          updatedFormData.additionalInfo[codeKey] = nameVal;
        }
        // If name is empty but code has value, copy to name
        if (!isEmpty(codeVal) && isEmpty(nameVal)) {
          updatedFormData.additionalInfo[nameKey] = codeVal;
        }
      });
      console.log('Step 2C: Synchronized name/code keys in additionalInfo');
    } catch (syncErr) {
      console.warn('Step 2C sync warning:', syncErr);
    }

    // 3. Handle document fields (controlled by env flag to disable document-based autofill)
    const ENABLE_EMPLOYEE_DOCUMENT_AUTOFILL = (process.env.REACT_APP_ENABLE_EMPLOYEE_DOCUMENT_AUTOFILL === 'true');
    console.log('Step 3: Checking for document fields to auto-populate. Enabled:', ENABLE_EMPLOYEE_DOCUMENT_AUTOFILL);
    
    // Define document field patterns to check
    const documentFieldPatterns = [
      'passport', 'visa', 'id', 'card', 'certificate', 'contract', 'photo', 'picture',
      'image', 'document', 'file', 'attachment', 'upload'
    ];
    
    // Define common document types that might be available from employee records
    const documentTypes = [
      'passportCopy', 'visaPage', 'birthCertificate', 'salaryCertificate', 
      'labourContract', 'emiratesId', 'photo', 'passport', 'visa', 'idCard',
      'certificate', 'contract', 'document', 'file'
    ];
    
    // Function to check if a field is likely a document field
    const isDocumentField = (fieldName, fieldType, fieldCategory) => {
      const lowerFieldName = fieldName.toLowerCase();
      const lowerFieldType = (fieldType || '').toLowerCase();
      const lowerFieldCategory = (fieldCategory || '').toLowerCase();
      
      return (
        lowerFieldType === 'file' || 
        lowerFieldType === 'document' || 
        lowerFieldCategory === 'documents' || 
        lowerFieldCategory.includes('document') ||
        lowerFieldCategory.includes('file') ||
        documentFieldPatterns.some(pattern => lowerFieldName.includes(pattern))
      );
    };
    
    // Process document fields
    const processDocumentFields = async () => {
      if (workflowFields && workflowFields.allFields) {
        const allFields = workflowFields.allFields;
        console.log(`Checking ${allFields.length} fields for document fields`);
        
        // First check if employee has documents
        let employeeDocuments = [];
        
        // Check if employee object already has documents
        if (employee.documents && Array.isArray(employee.documents)) {
          employeeDocuments = employee.documents;
          console.log(`Employee has ${employeeDocuments.length} documents in object`);
        } else {
          // Try to fetch documents from API
          try {
            console.log(`Fetching documents for employee ${employeeUid}`);
            
            // Get auth token from local storage
            const token = localStorage.getItem('token') || 
                        localStorage.getItem('jwt_token') || 
                        localStorage.getItem('authToken');
            
            if (!token) {
              console.error('Authentication token not found');
            } else {
              // Get the base URL from environment or current origin
              const apiBaseUrl = process.env.REACT_APP_API_URL || 
                               `${window.location.protocol}//${window.location.hostname}:8080`;
              
              // Try to fetch documents for this employee
              const response = await fetch(`${apiBaseUrl}/v1/users/${employeeUid}/documents`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (response.ok) {
                employeeDocuments = await response.json();
                console.log(`Fetched ${employeeDocuments.length} documents for employee ${employeeUid}`);
              } else {
                console.error(`Failed to fetch documents: ${response.status}`);
              }
            }
          } catch (error) {
            console.error('Error fetching employee documents:', error);
          }
        }
        
        // If we have documents, try to match them to fields
        if (employeeDocuments.length > 0) {
          debugLog('Processing document fields with available employee documents');
          
          // Find all document fields
          for (const field of allFields) {
            const fieldName = field.name || field.code || '';
            if (!fieldName) continue;
            
            // Skip if already populated
            if (updatedFormData.additionalInfo[fieldName] && 
                updatedFormData.additionalInfo[fieldName] !== '') {
              continue;
            }
            
            // Check if this is a document field
            if (isDocumentField(fieldName, field.type, field.category)) {
              debugLog(`Found document field: ${fieldName} (${field.type || 'unknown type'})`);
              
              // Try to find a matching document
              let matchingDoc = null;
              
              // First try exact match by document type
              matchingDoc = employeeDocuments.find(doc => 
                doc.documentType && doc.documentType.toLowerCase() === fieldName.toLowerCase()
              );
              
              // If no exact match, try partial matching
              if (!matchingDoc) {
                for (const docType of documentTypes) {
                  if (fieldName.toLowerCase().includes(docType.toLowerCase())) {
                    matchingDoc = employeeDocuments.find(doc => 
                      doc.documentType && doc.documentType.toLowerCase().includes(docType.toLowerCase())
                    );
                    if (matchingDoc) {
                      debugLog(`Found partial match for ${fieldName} with document type ${matchingDoc.documentType}`);
                      break;
                    }
                  }
                }
              }
              
              // If we found a matching document, create a document reference
              if (matchingDoc) {
                debugLog(`Found matching document for ${fieldName}:`, matchingDoc);
                
                // Check if this is a dynamic service field (UUID pattern)
                const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(matchingDoc.documentType);
                
                // Create a document reference object with necessary metadata
                const documentReference = {
                  isReference: true,                // Flag to indicate this is a reference to an existing document
                  id: matchingDoc.id || matchingDoc.filePath, // Document ID for backend association
                  uid: matchingDoc.uid || employeeUid,
                  documentType: matchingDoc.documentType, // Keep the original document type from the backend
                  fileName: matchingDoc.fileName || `${fieldName} Document`,
                  contentType: matchingDoc.contentType || 'application/pdf',
                  fileSize: matchingDoc.fileSize || 0,
                  // Source employee UID - critical for document viewing
                  employeeUid: employeeUid,
                  filePath: matchingDoc.filePath || matchingDoc.id,
                  // Add document URL for tracking - but only for static fields
                  documentUrl: isDynamicServiceField ? 
                    (matchingDoc.filePath ? `/v1/files/download?filePath=${encodeURIComponent(matchingDoc.filePath)}` : null) :
                    `/v1/users/${employeeUid}/documents/${matchingDoc.documentType}/download`
                };
                
                // Set the field value in the form data
                const lowerName = fieldName.toLowerCase();
                if (lowerName === 'emiratesid') {
                  // Never attach document reference to the text field
                  // Prefer assigning to emiratesIdCopy if present
                  const hasCopyField = allFields.some(f => ((f.name || f.code || '').toLowerCase() === 'emiratesidcopy'));
                  if (hasCopyField) {
                    debugLog('Assigning Emirates ID document to emiratesIdCopy file field');
                    updatedFormData.additionalInfo['emiratesIdCopy'] = documentReference;
                  } else {
                    // As a fallback, do not assign to emiratesId; skip to avoid wrong UI type
                    debugLog('emiratesIdCopy field not present; skipping assignment to avoid mis-typing emiratesId');
                  }
                } else {
                  updatedFormData.additionalInfo[fieldName] = documentReference;
                }
                
                console.log(`Auto-filled document field ${fieldName} with document reference:`, documentReference);
              } else {
                console.log(`No matching document found for field ${fieldName}`);
              }
            }
          }
        } else {
          console.log('No employee documents available for auto-filling');
        }
      }
    };
    
    // Execute document field processing only when explicitly enabled
    if (ENABLE_EMPLOYEE_DOCUMENT_AUTOFILL) {
      await processDocumentFields();
    } else {
      console.log('Employee document-based autofill is disabled by configuration; skipping Step 3.');
    }
    
    // 4. Apply a final check for any static fields that weren't mapped
    console.log('Step 4: Final check for any unmapped static fields');
    if (workflowFields && workflowFields.allFields) {
      const staticFields = workflowFields.allFields.filter(field => field.isStatic);
      console.log(`Found ${staticFields.length} static fields to check`);
      
      staticFields.forEach(field => {
        const fieldName = field.name || field.code || '';
        if (!fieldName) return;
        
        // Skip if already populated
        if (updatedFormData.additionalInfo[fieldName] && updatedFormData.additionalInfo[fieldName] !== '') {
          console.log(`Static field ${fieldName} already populated, skipping`);
          return;
        }
        
        // Try to find a match in employee data
        for (const [key, value] of Object.entries(employee)) {
          if (key.toLowerCase() === fieldName.toLowerCase() && value !== null && value !== undefined && value !== '') {
            console.log(`Found direct match for static field ${fieldName} in employee data:`, value);
            updatedFormData.additionalInfo[fieldName] = value;
            break;
          }
        }
      });
    }

    debugLog('Final updated form data with employee information:', updatedFormData);

    // Address is always sourced from the customer organisation (not the
    // employee). Override whatever the employee-utility mapping set. Light
    // fetch — uses bare TOrganisation, not TTicketOrgDetails.
    try {
      const orgAddress = await fetchOrganisationAddress(organizationId);
      if (updatedFormData && updatedFormData.additionalInfo) {
        if (orgAddress) {
          updatedFormData.additionalInfo.address = orgAddress;
        } else {
          // Org has no address — leave blank (no fallback to employee.address)
          updatedFormData.additionalInfo.address = '';
        }
      }
    } catch (e) {
      console.warn('Failed to auto-fill address from organisation:', e?.message || e);
    }

    // Sanitize: prevent employeeId corruption in document number fields
    try {
      const sanitizeDocNumberFields = [
        'emiratesId',
        'emiratesIdNumber',
        'emiratesIdIssueDate',
        'emiratesIdExpiryDate',
        'passportNumber',
        'visaNumber'
      ];
      const empId = (employee.employeeId || employee.id || '').toString();
      if (updatedFormData && updatedFormData.additionalInfo && empId) {
        sanitizeDocNumberFields.forEach((key) => {
          const v = updatedFormData.additionalInfo[key];
          if (typeof v === 'string' && v === empId) {
            debugLog(`[Sanitize] Clearing '${key}' incorrectly set to employeeId (${empId})`);
            updatedFormData.additionalInfo[key] = '';
          }
        });
      }
    } catch (e) {
      // non-fatal
    }
    
    // Update global form data reference for WorkflowFileField component
    window.currentFormData = updatedFormData;
    
    // Set form data state
    setFormData(updatedFormData);

    // Force a re-render so React picks up nested document-field changes. IMPORTANT: use a FUNCTIONAL
    // update that re-serializes the LATEST state — the previous version re-applied the captured
    // snapshot (name, no docs) and reset window.currentFormData ~100ms later, which clobbered the
    // grouped-document autofill that runs asynchronously right after selection (making the fields
    // flash in and then disappear).
    setTimeout(() => {
      setFormData(currentFormData => JSON.parse(JSON.stringify(currentFormData)));
    }, 150);
  };

  // Handle toggle change for step assignment type
  const handleStepAssignmentTypeToggle = (stepId, event) => {
    const isChecked = event.target.checked;
    const newType = isChecked ? 'organization' : 'thaklis';
    
    console.log(`Toggling step ${stepId} assignment type to: ${newType}`);
    
    // Update assignment state
    setStepAssignmentTypes(prev => {
      const newState = {
        ...prev,
        [stepId]: newType
      };
      return newState;
    });
    
    // If the cost dialog is open, recalculate costs immediately
    if (showCostDialog) {
      // Short timeout to ensure state update has completed
      setTimeout(() => {
        const updatedCosts = calculateServiceCosts();
        setServiceCosts(updatedCosts);
        console.log('Updated costs after toggle:', updatedCosts);
      }, 50);
    }
    
    // Clear any existing assignments for this step
    if (assignedUsers[stepId]) {
      setAssignedUsers(prev => {
        const newAssignedUsers = { ...prev };
        delete newAssignedUsers[stepId];
        return newAssignedUsers;
      });
    }
  };

  // Create context value with all necessary state and functions
  const contextValue = {
    selectedEmployee,
    formData,
    workflowFields,
    handleEmployeeSelect,
    setFormData
  };

  return (
    <NewWorkflowRequestContext.Provider value={contextValue}>
      <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <IconButton onClick={handleBack} size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box>
          <Typography component="h1" sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 }}>
            New Workflow Request
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mt: 0.25 }}>
            Choose a workflow, select the {selectedTargetType === 'ORGANIZATION' ? 'organization' : 'employee'}, and submit the request.
          </Typography>
        </Box>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Request created successfully!
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {requestNumber && (
              <Typography variant="body2">
                <strong>Organization Request Number:</strong> {response?.formattedOrgRequestNumber || `${response?.organizationRequestPrefix || ''}-R-${requestNumber}`}
              </Typography>
            )}
            {response?.systemRequestNumber && (
              <Typography variant="body2">
                <strong>System Request Number:</strong> {response?.formattedSystemRequestNumber || response?.systemRequestNumber}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium', color: 'primary.main' }}>
              <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              Important: Your request has been created but the workflow has not been started yet.
            </Typography>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => navigate(`/workflow-requests/${response?.id}`)}
              startIcon={<PlayArrowIcon />}
            >
              View Request & Start Workflow
            </Button>
          </Box>
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle1">{error.message}</Typography>
          {error.details && (
            <Typography variant="body2" color="text.secondary">
              Error details: {error.details} {error.status && `(${error.status})`}
            </Typography>
          )}
        </Alert>
      )}

      <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
        {/* Target Type Selection */}
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', mb: 1.25 }}>
          Request Type
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Chip
            label="Employee"
            color={selectedTargetType === 'EMPLOYEE' ? 'primary' : 'default'}
            onClick={() => { setSelectedTargetType('EMPLOYEE'); setSelectedWorkflow(''); setWorkflowDetails(null); }}
            variant={selectedTargetType === 'EMPLOYEE' ? 'filled' : 'outlined'}
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label="Organization"
            color={selectedTargetType === 'ORGANIZATION' ? 'primary' : 'default'}
            onClick={() => { setSelectedTargetType('ORGANIZATION'); setSelectedWorkflow(''); setWorkflowDetails(null); }}
            variant={selectedTargetType === 'ORGANIZATION' ? 'filled' : 'outlined'}
            sx={{ cursor: 'pointer' }}
          />
        </Box>

        {/* Workflow Selection Panel - Tile-based selection (shown only after target type is selected) */}
        {selectedTargetType ? (
          <WorkflowSelectionPanel
            workflows={assignedWorkflows.filter(a => {
              const wfTargetType = a.workflow?.targetType || 'EMPLOYEE';
              return wfTargetType === selectedTargetType;
            })}
            selectedWorkflowId={selectedWorkflow}
            onWorkflowSelect={handleWorkflowTileSelect}
            autoCollapse={true}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            Please select a request type above to view available workflows.
          </Typography>
        )}
        
        {/* Cost Summary Display is now available via the 'Show Estimated Cost Breakdown' button after workflow steps */}

        {workflowDetails && (
          <>

            {/* Employee Assignment Section - only for EMPLOYEE workflows */}
            {selectedTargetType === 'EMPLOYEE' && (
            <>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '0.9375rem', fontWeight: 700, mt: 0.5, mb: 1.5, display: 'flex', alignItems: 'center' }}>
              Assign Employee
              <Tooltip title="Assign an employee to handle this workflow request">
                <InfoIcon fontSize="small" sx={{ ml: 1, color: 'info.main', fontSize: '16px' }} />
              </Tooltip>
            </Typography>
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ position: 'relative' }}>
                  {!selectedEmployee ? (
                    /* Employee search component when no employee is selected */
                    <>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <QuickEmployeeSearch
                            selectedEmployee={null}
                            onEmployeeSelect={handleEmployeeSelect}
                          />
                        </Box>
                        <Button
                          variant="outlined"
                          onClick={() => setOpenQuickAddEmployee(true)}
                          sx={{
                            minWidth: 'auto',
                            height: '56px',
                            px: 2,
                            borderRadius: '10px',
                            borderColor: '#00838f',
                            color: '#00838f',
                            textTransform: 'none',
                            fontWeight: 600,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: '#00838f',
                              backgroundColor: 'rgba(0,131,143,0.04)',
                              boxShadow: '0 0 0 3px rgba(0,131,143,0.1)'
                            }
                          }}
                        >
                          Quick Add
                        </Button>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                        <InfoIcon fontSize="small" sx={{ mr: 0.5, fontSize: '16px' }} />
                        Search for an employee by name, ID, or email to assign to this workflow request
                      </Typography>
                    </>
                  ) : (
                    /* Selected Employee Display */
                    <>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                        borderRadius: '8px',
                        backgroundColor: 'background.default',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,131,143,0.12)',
                          backgroundColor: 'action.hover'
                        }
                      }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            mr: 2,
                            bgcolor: 'primary.main',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {/* Inline base64 thumbnail first (zero HTTP fetch);
                              fall back to S3 fetch only for legacy rows. */}
                          {selectedEmployee.photoThumbnail ? (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <img
                                src={selectedEmployee.photoThumbnail}
                                alt={`${selectedEmployee.nameEn || selectedEmployee.firstNameEn || 'Employee'} photo`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '50%'
                                }}
                              />
                            </Box>
                          ) : selectedEmployee.photo ? (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <AuthenticatedImage
                                src={selectedEmployee.photo}
                                alt={`${selectedEmployee.nameEn || selectedEmployee.firstNameEn || 'Employee'} photo`}
                                employeeId={selectedEmployee.uid || selectedEmployee.employeeId || ''}
                                maxSize={80}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '50%'
                                }}
                              />
                            </Box>
                          ) : (
                            selectedEmployee.name ? selectedEmployee.name.charAt(0) :
                            selectedEmployee.firstNameEn ? selectedEmployee.firstNameEn.charAt(0) :
                            <MdPerson size={24} />
                          )}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            {selectedEmployee.name || 
                             `${selectedEmployee.firstNameEn || ''} ${selectedEmployee.lastNameEn || ''}` || 
                             'Unknown'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Chip 
                              size="small" 
                              label={selectedEmployee.uid || selectedEmployee.employeeId || 'No ID'}
                              sx={{ mr: 1, backgroundColor: 'primary.light', color: 'primary.dark', fontSize: '0.75rem' }}
                            />
                            {selectedEmployee.department && (
                              <Typography variant="body2" color="text.secondary">
                                {selectedEmployee.department?.departmentName}
                              </Typography>
                            )}
                          </Box>

                        </Box>
                        <Tooltip title="Remove assigned employee">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEmployeeSelect(null)}
                            sx={{ 
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'error.main',
                                backgroundColor: 'rgba(244, 67, 54, 0.08)'
                              }
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      {/* Phase 2A: Read-only Grouped Employee Documents Panel (behind feature flag)
                      {FEATURE_WORKFLOW_DOC_GROUPS && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                            Employee Documents (Grouped)
                          </Typography>
                          <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                            <CardContent>
                              
                              {groupedDocsLoading && (
                                <Typography variant="body2" color="text.secondary">
                                  Loading grouped documents...
                                </Typography>
                              )}

                              
                              {!groupedDocsLoading && groupedDocsError && (
                                <Typography variant="body2" color="error">
                                  {groupedDocsError}
                                </Typography>
                              )}

                              
                              {!groupedDocsLoading && !groupedDocsError && Array.isArray(groupedDocs) && groupedDocs.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  No documents found for this employee.
                                </Typography>
                              )}

                              
                              {!groupedDocsLoading && !groupedDocsError && Array.isArray(groupedDocs) && (
                                (() => {
                                  const currentGroups = groupedDocs.filter(g => (g?.common?.isCurrent === true) && !!g?.documentGroupId);
                                  if (currentGroups.length === 0) {
                                    return (
                                      <Typography variant="body2" color="text.secondary">
                                        No current document groups found for this employee.
                                      </Typography>
                                    );
                                  }
                                  
                                  const byType = new Map();
                                  currentGroups.forEach((g) => {
                                    const key = mapDocTypeToKey(g?.common?.documentType || g?.documentType);
                                    if (!byType.has(key)) byType.set(key, []);
                                    byType.get(key).push(g);
                                  });
                                  return (
                                    <Box>
                                      {Array.from(byType.entries()).map(([docKey, groups]) => (
                                        <Box key={`type-${docKey}`} sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{docKey}</Typography>
                                          {groups.map((group, idx) => {
                                            const common = group?.common || {};
                                            const docs = Array.isArray(group?.documents) ? group.documents : (Array.isArray(group?.files) ? group.files : []);
                                            const category = (common.documentType || group.documentType || 'Documents')
                                              .toString()
                                              .replace(/_/g, ' ')
                                              .replace(/\b\w/g, (c) => c.toUpperCase());
                                            const groupId = group?.documentGroupId;
                                            const selected = groupedSelections[docKey]?.documentGroupId === groupId;
                                            return (
                                              <Box key={`grp-${groupId || idx}`} sx={{ mb: 1.25, pl: 1, borderLeftWidth: 2, borderLeftStyle: 'solid', borderLeftColor: 'divider' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                                                  <FormControlLabel
                                                    control={<Radio checked={!!selected} onChange={() => handleSelectGroup(docKey, group)} />}
                                                    label={
                                                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <Chip size="small" label={category} sx={{ mr: 1, mb: 0.5 }} />
                                                        {groupId && (
                                                          <Chip size="small" color="default" variant="outlined" label={`Group: ${groupId}`} sx={{ mr: 1, mb: 0.5 }} />
                                                        )}
                                                        {common?.isCurrent && (
                                                          <Chip size="small" color="success" label="Current" sx={{ mr: 1, mb: 0.5 }} />
                                                        )}
                                                        {common?.expiryDate && (
                                                          <Chip size="small" color="warning" variant="outlined" label={`Expiry: ${common.expiryDate}`} sx={{ mr: 1, mb: 0.5 }} />
                                                        )}
                                                        {common?.documentNumber && (
                                                          <Chip size="small" color="info" variant="outlined" label={`No: ${common.documentNumber}`} sx={{ mr: 1, mb: 0.5 }} />
                                                        )}
                                                      </Box>
                                                    }
                                                  />
                                                </Box>
                                                {docs.length === 0 ? (
                                                  <Typography variant="caption" color="text.secondary">No files</Typography>
                                                ) : (
                                                  <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                                    {docs.map((d, i) => {
                                                      const fileName = d?.fileName || d?.name || d?.originalName || 'document';
                                                      const filePath = d?.filePath || d?.path || d?.documentUrl || d?.s3Key || null;
                                                      const docSelected = selected && groupedSelections[docKey]?.selectedDocumentId === d?.id;
                                                      return (
                                                        <li key={`doc-${groupId || idx}-${i}`}>
                                                          <FormControlLabel
                                                            control={<Radio checked={!!docSelected} onChange={() => handleSelectDoc(docKey, group, d)} />}
                                                            label={
                                                              <Typography variant="body2" component="span">
                                                                {fileName}
                                                              </Typography>
                                                            }
                                                          />
                                                          {filePath && (
                                                            <>
                                                              {' '}
                                                              <Button size="small" variant="text" onClick={() => handleGroupedFileDownload(filePath, fileName)} sx={{ textTransform: 'none', fontSize: '0.85rem', p: 0 }}>
                                                                Download
                                                              </Button>
                                                            </>
                                                          )}
                                                        </li>
                                                      );
                                                    })}
                                                  </ul>
                                                )}
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      ))}
                                    </Box>
                                  );
                                })()
                              )}
                              Phase 3A: Live JSON preview of current selections (no submit)
                              {FEATURE_WORKFLOW_DOC_GROUPS && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Selected Documents (Preview)</Typography>
                                  <Paper variant="outlined" sx={{ p: 1, bgcolor: 'common.white' }}>
                                    <pre style={{ margin: 0, maxHeight: 240, overflow: 'auto' }}>
                                      {JSON.stringify(groupedSelections, null, 2)}
                                    </pre>
                                  </Paper>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Box>
                      )} */}
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
            </>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom sx={{ fontSize: '0.9375rem', fontWeight: 700, mt: 0.5, mb: 1.5, color: 'text.primary' }}>
              Request Details
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <TextField
                  name="requestTitle"
                  label="Request Title"
                  fullWidth
                  value={formData.requestTitle}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Description"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="priority-select-label">Priority</InputLabel>
                  <Select
                    labelId="priority-select-label"
                    id="priority-select"
                    name="priority"
                    value={formData.priority}
                    label="Priority"
                    onChange={handleInputChange}
                  >
                    <MenuItem value="LOW">Low</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="URGENT">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            {/* Display workflow fields if available */}
            {(workflowFields.customerInfoFields.length > 0 || workflowFields.formFields.length > 0) && (
              <>
                {/* Customer Info Fields Section */}
                {workflowFields.customerInfoFields.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontSize: '0.9375rem', fontWeight: 700, mt: 0.5, mb: 1.5, color: 'text.primary' }}>
                      Customer Information
                    </Typography>
                    <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          {workflowFields.customerInfoFields.map((field, index) => {
                            // Safely get field name with fallback.
                            // Coded dynamic fields are keyed by their title (like null-code fields) so the
                            // ticket shows the readable title rather than the field_xxxx code.
                            const fieldCode = field.code || '';
                            const isDynamicField = fieldCode.startsWith('field_');
                            const fieldName = isDynamicField ? (field.label || field.title || fieldCode) : (field.name || field.code || field.label || `field_${index}`);
                            const fieldValue = formData.additionalInfo[fieldName] || '';
                            const hasError = !!validationErrors[fieldName];
                            const errorMessage = validationErrors[fieldName] || '';
                            
                            // Map field to the format expected by FieldFactory
                            const fieldConfig = {
                              id: `customer-field-${index}`,
                              name: fieldName,
                              label: field.label || fieldName,
                              required: field.required,
                              description: field.description || '',
                              // Special handling for field types
                              type: getFieldType(field),
                              options: field.options || [
                                // Default options for gender fields
                                ...((typeof fieldName === 'string' && fieldName.toLowerCase().includes('gender')) || (typeof fieldName === 'string' && fieldName.toLowerCase().includes('sex')) ? 
                                  [{ value: 'Male', label: 'Male' }, { value: 'Female', label: 'Female' }] : [])
                              ]
                            };
                            
                            return (
                              <Grid item xs={12} sm={6} key={`customer-field-${index}`}>
                                <FormControl fullWidth error={hasError}>
                                  <FieldFactory
                                  field={fieldConfig}
                                  value={fieldConfig.type === 'file' ? (fieldValue || null) : fieldValue}
                                  onChange={(e) => {
                                    // Special handling for date fields
                                    if (fieldConfig.type === 'date' && e.target.value instanceof Date) {
                                      handleDateChange(fieldName, e.target.value);
                                    } else if (fieldConfig.type === 'file') {
                                      // For file fields, we pass the File object directly
                                      handleAdditionalInfoChange(fieldName, e, fieldConfig.type.toUpperCase());
                                    } else {
                                      handleAdditionalInfoChange(fieldName, e.target.value, fieldConfig.type.toUpperCase());
                                    }
                                  }}
                                  onBlur={() => validateField(fieldName, fieldValue, fieldConfig.type.toUpperCase())}
                                  error={hasError}
                                  helperText={errorMessage}
                                />
                                </FormControl>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </CardContent>
                    </Card>
                  </>
                )}
                
                {/* Form Fields Section with Categorized Fields */}
                {workflowFields.formFields.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontSize: '0.9375rem', fontWeight: 700, mt: 0.5, mb: 1.5, color: 'text.primary' }}>
                      Service Information
                    </Typography>
                    
                    {/* Debug information - will print what we're sending to CategorizedFields */}
                    {/* Debug logs removed */}
                    
                    {/* Debug helper to verify field values are being passed correctly */}
                    {(() => {
                      if (selectedEmployee && workflowFields.formFields) {
                        // Debug helper removed
                      }
                      return null;
                    })()}
                    
                    {/* Debug information - current form data state */}
                    <Box sx={{ mb: 2, display: 'none' }}>
                      <pre>{JSON.stringify(formData, null, 2)}</pre>
                    </Box>
                    
                    {/* Using CategorizedFields component for better organization */}
                    <CategorizedFields
                      fields={workflowFields.formFields}
                      formData={formData} /* Pass the entire formData object to ensure additionalInfo is accessible */
                      validationErrors={validationErrors}
                      getFieldType={getFieldType}
                      validateField={validateField}
                      handleDateChange={(fieldName, value) => handleDateChange(fieldName, value)}
                      handleAdditionalInfoChange={handleAdditionalInfoChange}
                      dbCategories={fieldCategories}
                    />
                  </>
                )}
              </>
            )}

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom sx={{ fontSize: '0.9375rem', fontWeight: 700, mt: 0.5, mb: 1.5, color: 'text.primary' }}>
              Workflow Steps
            </Typography>
            
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontSize: pxToRem(14), fontWeight: 'medium' }}>
                    Select workflow steps to assign to your organization:
                  </Typography>
                </Box>
                {console.log('RENDER WorkflowSteps - context:', {
                  selectedWorkflow,
                  hasWorkflowDetails: !!workflowDetails,
                  stepsArrayType: workflowDetails && workflowDetails.steps ? typeof workflowDetails.steps : 'n/a',
                  stepsLength: workflowDetails && workflowDetails.steps ? workflowDetails.steps.length : 0,
                  firstStepSummary: workflowDetails && Array.isArray(workflowDetails.steps) && workflowDetails.steps.length > 0 ?
                    { id: workflowDetails.steps[0].id, name: workflowDetails.steps[0].name || workflowDetails.steps[0].serviceName } : null,
                  stepAssignmentTypes
                })}
                
                {workflowDetails && workflowDetails.steps && Array.isArray(workflowDetails.steps) && workflowDetails.steps.length > 0 ? (
                  workflowDetails.steps.map((step, index) => {
                    // Default to Thaklis assignment (false) if not set
                    const assignmentType = stepAssignmentTypes[step.id] || 'thaklis';
                    const isOrgAssignment = assignmentType === 'organization';
                    console.log('RENDER WorkflowSteps - step row:', {
                      idx: index,
                      stepId: step.id,
                      stepName: step.name || step.serviceName,
                      serviceName: step.serviceName,
                      derivedAssignmentType: assignmentType,
                      isOrgAssignment
                    });
                    
                    return (
                      <Box key={step.id || `step-${index}`} sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        py: 1.5, 
                        px: 1,
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                        bgcolor: isOrgAssignment ? 'rgba(0, 127, 255, 0.04)' : 'transparent',
                        borderRadius: 1,
                        '&:hover': { bgcolor: isOrgAssignment ? 'rgba(0, 127, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)' }
                      }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontSize: pxToRem(14), fontWeight: 'bold' }}>
                            {index + 1}. {step.name || step.serviceName}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: pxToRem(13) }}>
                            Service: {step.serviceName}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Tooltip title={isOrgAssignment ? 
                              `Currently assigned to ${organizationName}: The organization will be responsible for this step.` :
                              'Currently assigned to Thaklis: Thaklis staff will handle this step.'
                            }>
                              <Chip
                                icon={isOrgAssignment ? <PersonIcon /> : <AccessTimeIcon />}
                                label={isOrgAssignment ? `Assigned to ${organizationName}` : 'Assigned to Thaklis'}
                                size="small"
                                color={isOrgAssignment ? 'primary' : 'secondary'}
                                variant="outlined"
                                sx={{ height: '24px', minWidth: '150px' }}
                              />
                            </Tooltip>
                          </Box>
                          {canAssign ? (
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={isOrgAssignment}
                                  onChange={(e) => handleStepAssignmentTypeToggle(step.id, e)}
                                  color="primary"
                                />
                              }
                              label="Toggle Assignment"
                              labelPlacement="start"
                              sx={{
                                mb: 0,
                                mr: 0,
                                '& .MuiFormControlLabel-label': { fontSize: pxToRem(14) }
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ fontSize: pxToRem(13), fontStyle: 'italic' }}
                            >
                              No permission to change assignment
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      No workflow steps found. Please make sure the workflow is properly configured.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Debug info for cost calculator visibility */}
            {console.log('Cost calculator button visibility check:', {
              workflowDetailsExists: !!workflowDetails,
              stepsExist: !!(workflowDetails && workflowDetails.steps),
              stepsLength: workflowDetails && workflowDetails.steps ? workflowDetails.steps.length : 0,
              servicesEnabled: servicesEnabled
            })}
            
            {/* Disabled for now
            Always show Cost Estimation Button - Add cost calculator button regardless of conditions
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<CalculateIcon />}
                onClick={handleShowCostDialog}
                sx={{ borderRadius: 1 }}
              >
                Show Estimated Cost Breakdown
              </Button>
            </Box> */}

            {/* Additional Information section has been hidden as per requirements */}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: 4, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant="text"
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                sx={{ color: 'text.secondary' }}
              >
                Back to Requests
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                endIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                disabled={submitting}
                sx={{ px: 4, fontWeight: 600, borderRadius: 1.5 }}
              >
                {submitting ? 'Submitting…' : 'Submit Request'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
      
      {/* Customer Creation Dialog */}
      <Dialog
        open={openCustomerDialog}
        onClose={handleCloseCustomerDialog}
        aria-labelledby="customer-dialog-title"
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle id="customer-dialog-title" sx={{ bgcolor: 'background.default', borderBottom: '1px solid', borderBottomColor: 'divider' }}>
          Customer Not Found - Add New Customer
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
            <DialogContentText sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center' }}>
              Customer UID: <Box component="span" sx={{ ml: 1, p: 0.5, bgcolor: 'primary.light', borderRadius: 0.5, fontFamily: 'monospace' }}>{newCustomer.uid}</Box>
            </DialogContentText>
          </Box>
          <DialogContentText sx={{ mb: 2 }}>
            Please enter the customer details below to add them to the system and proceed with the workflow request.
          </DialogContentText>
          <DialogContentText sx={{ mb: 1, color: 'text.primary', fontWeight: 'medium' }}>
            Fields marked with <Box component="span" sx={{ color: 'error.main' }}>*</Box> are required. After adding the customer, you'll be able to continue creating the workflow request.
          </DialogContentText>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="firstNameEn"
                label="First Name"
                value={newCustomer.firstNameEn}
                onChange={handleCustomerInputChange}
                fullWidth
                required
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="middleNameEn"
                label="Middle Name"
                value={newCustomer.middleNameEn}
                onChange={handleCustomerInputChange}
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="lastNameEn"
                label="Last Name"
                value={newCustomer.lastNameEn}
                onChange={handleCustomerInputChange}
                fullWidth
                required
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="nameEn"
                label="Full Name"
                value={newCustomer.nameEn}
                onChange={handleCustomerInputChange}
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="mobileNumber"
                label="Mobile Number"
                value={newCustomer.mobileNumber}
                onChange={handleCustomerInputChange}
                fullWidth
                required
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="email"
                label="Email"
                type="email"
                value={newCustomer.email}
                onChange={handleCustomerInputChange}
                fullWidth
                margin="dense"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomerDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleCreateCustomer} color="primary" variant="contained">
            Create Customer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Workflow Step Assignment Dialog */}
      <WorkflowStepAssignDialog
        open={assignmentDialogOpen}
        onClose={handleCloseAssignDialog}
        step={currentStepForAssignment}
        onAssign={handleAssignmentComplete}
        jwt={localStorage.getItem('jwt_token')}
      />
      
      {/* Cost Breakdown Dialog */}
      <Dialog 
        open={showCostDialog} 
        onClose={handleCloseCostDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'common.white', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalculateIcon sx={{ mr: 1 }} />
            Estimated Cost Breakdown
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          {serviceCosts && serviceCosts.services && serviceCosts.services.length > 0 ? (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontSize: pxToRem(14), fontWeight: 'medium' }}>
                Workflow: {workflowDetails?.name || 'Selected Workflow'}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom sx={{ fontSize: pxToRem(14), fontWeight: 'medium' }}>
                Services & Associated Costs
              </Typography>
              
              <Grid container spacing={2}>
                {serviceCosts.services.map((service, index) => (
                  <Grid item xs={12} key={`service-${index}`}>
                    <Card variant="outlined" sx={{ mb: 1 }}>
                      <CardContent sx={{ pb: '16px !important' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography variant="h6" gutterBottom sx={{ fontSize: pxToRem(14), fontWeight: 'medium' }}>
                            {service.name || `Service ${index + 1}`}
                          </Typography>
                          <Chip 
                            label={service.isOrgAssignment ? `Assigned to ${organizationName}` : 'Assigned to Thaklis'}
                            size="small"
                            color={service.isOrgAssignment ? 'success' : 'primary'}
                            variant="outlined"
                          />
                        </Box>
                        
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 1 }}>
                            <Typography variant="body2" color="textSecondary">Government Fee:</Typography>
                            <Typography variant="body2" fontWeight="medium">${service.govtCharge.toFixed(2)}</Typography>
                          </Box>
                          
                          {!service.isOrgAssignment && (
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 1 }}>
                                <Typography variant="body2" color="textSecondary">Service Charge:</Typography>
                                <Typography variant="body2" fontWeight="medium">${service.serviceCharge.toFixed(2)}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 1 }}>
                                <Typography variant="body2" color="textSecondary">Tax (5%):</Typography>
                                <Typography variant="body2" fontWeight="medium">${service.taxAmount.toFixed(2)}</Typography>
                              </Box>
                            </>
                          )}
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 1, borderTop: '1px dashed', borderTopColor: 'divider' }}>
                            <Typography variant="body2" fontWeight="medium" color="textSecondary">Total for this step:</Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              ${(service.govtCharge + service.serviceCharge + service.taxAmount).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom sx={{ fontSize: pxToRem(14), fontWeight: 'medium' }}>
                Total Cost Summary
              </Typography>
              
              <Paper elevation={2} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Total Government Charges:</Typography>
                    <Typography variant="h6">${serviceCosts.totalGovtCharge.toFixed(2)}</Typography>
                  </Grid>
                  
                  {serviceCosts.totalServiceCharge > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textSecondary">Total Service Charges:</Typography>
                      <Typography variant="h6">${serviceCosts.totalServiceCharge.toFixed(2)}</Typography>
                    </Grid>
                  )}
                  
                  {serviceCosts.totalTax > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textSecondary">Total Tax (5%):</Typography>
                      <Typography variant="h6">${serviceCosts.totalTax.toFixed(2)}</Typography>
                    </Grid>
                  )}
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">Grand Total:</Typography>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      ${(serviceCosts.totalServiceCharge + serviceCosts.totalGovtCharge + serviceCosts.totalTax).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
              
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
                Note: Costs are estimated and may vary based on additional services or changes to the workflow.
              </Typography>
            </>
          ) : (
            <Alert severity="info">
              No cost information available for this workflow. Please select a workflow with associated services.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseCostDialog} variant="contained" sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Document Upload Dialog - This is handled by the WorkflowFileField component */}
      
      {/* File Viewer Dialog */}
      <Dialog 
        open={fileViewerDialog.open} 
        onClose={handleCloseFileViewer} 
        maxWidth={false} 
        fullWidth 
        PaperProps={{
          sx: {
            height: '80vh',
            width: '95vw'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{fileViewerDialog.fileName}</Typography>
            <Box>
              {fileViewerDialog.fileUrl && (
                <Tooltip title="Download File">
                  <IconButton 
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = fileViewerDialog.fileUrl;
                      link.download = fileViewerDialog.fileName;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    sx={{ mr: 1 }}
                  >
                    <GetAppIcon />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton onClick={handleCloseFileViewer}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {fileViewerDialog.loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ) : fileViewerDialog.error ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%" flexDirection="column">
              <Typography variant="h6" color="error" gutterBottom>Error</Typography>
              <Typography variant="body1">{fileViewerDialog.error}</Typography>
            </Box>
          ) : fileViewerDialog.fileType.startsWith('image/') ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <img 
                src={fileViewerDialog.fileUrl} 
                alt={fileViewerDialog.fileName} 
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
              />
            </Box>
          ) : isPdfFile(fileViewerDialog.fileName, fileViewerDialog.fileType) ? (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Primary PDF viewer using object tag */}
              <Box sx={{ flexGrow: 1, width: '100%', position: 'relative', overflow: 'hidden' }}>
                <object
                  data={fileViewerDialog.fileUrl}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                >
                  {/* Fallback content if object tag fails */}
                  <iframe
                    src={fileViewerDialog.fileUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                    title="PDF Viewer"
                  >
                    <p>Your browser does not support PDF viewing.</p>
                  </iframe>
                </object>
              </Box>
              
              {/* Controls for PDF */}
              <Box sx={{ p: 2, borderTop: '1px solid', borderTopColor: 'divider', display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<GetAppIcon />}
                  onClick={async () => {
                    try {
                      const url = fileViewerDialog.fileUrl;
                      if (url && url.startsWith('blob:')) {
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = fileViewerDialog.fileName || 'document.pdf';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        return;
                      }
                      const token = localStorage.getItem('jwt_token') || (localStorage.getItem('authInfo') ? JSON.parse(localStorage.getItem('authInfo')).token : null) || localStorage.getItem('token') || localStorage.getItem('authToken');
                      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                      if (!res.ok) throw new Error(`Download failed: ${res.status} ${res.statusText}`);
                      const blob = await res.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = blobUrl;
                      link.download = fileViewerDialog.fileName || 'document.pdf';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
                    } catch (e) {
                      console.error('Error downloading PDF', e);
                    }
                  }}
                >
                  Download PDF
                </Button>
                <Button
                  variant="outlined"
                  onClick={async () => {
                    try {
                      const url = fileViewerDialog.fileUrl;
                      if (url && url.startsWith('blob:')) {
                        window.location.href = url;
                        return;
                      }
                      const token = localStorage.getItem('jwt_token') || (localStorage.getItem('authInfo') ? JSON.parse(localStorage.getItem('authInfo')).token : null) || localStorage.getItem('token') || localStorage.getItem('authToken');
                      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                      if (!res.ok) throw new Error(`Open failed: ${res.status} ${res.statusText}`);
                      const blob = await res.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      window.location.href = blobUrl;
                    } catch (e) {
                      console.error('Error opening PDF', e);
                    }
                  }}
                >
                  Open
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* For other file types, show file info and actions */}
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  File Preview
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  This file type cannot be previewed directly in this dialog.
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Filename: {fileViewerDialog.fileName || 'document'}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  File type: {fileViewerDialog.fileType || 'Unknown'}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<GetAppIcon />}
                    onClick={async () => {
                      try {
                        const url = fileViewerDialog.fileUrl;
                        if (url && url.startsWith('blob:')) {
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = fileViewerDialog.fileName || 'document';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          return;
                        }
                        const token = localStorage.getItem('jwt_token') || (localStorage.getItem('authInfo') ? JSON.parse(localStorage.getItem('authInfo')).token : null) || localStorage.getItem('token') || localStorage.getItem('authToken');
                        const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                        if (!res.ok) throw new Error(`Download failed: ${res.status} ${res.statusText}`);
                        const blob = await res.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = fileViewerDialog.fileName || 'document';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
                      } catch (e) {
                        console.error('Error downloading file', e);
                      }
                    }}
                  >
                    Download File
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={async () => {
                      try {
                        const url = fileViewerDialog.fileUrl;
                        if (url && url.startsWith('blob:')) {
                          window.location.href = url;
                          return;
                        }
                        const token = localStorage.getItem('jwt_token') || (localStorage.getItem('authInfo') ? JSON.parse(localStorage.getItem('authInfo')).token : null) || localStorage.getItem('token') || localStorage.getItem('authToken');
                        const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                        if (!res.ok) throw new Error(`Open failed: ${res.status} ${res.statusText}`);
                        const blob = await res.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        window.location.href = blobUrl;
                      } catch (e) {
                        console.error('Error opening file', e);
                      }
                    }}
                  >
                    Open
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <QuickEmployeeAddDialog
        open={openQuickAddEmployee}
        onClose={() => setOpenQuickAddEmployee(false)}
        onEmployeeAdded={handleEmployeeAdded}
      />
      {/* Debug overlay and toggle button removed */}
    </Box>
    </NewWorkflowRequestContext.Provider>
  );
};

// Debug Overlay Component for Employee Auto-Population Debugging
// Debug overlay component removed

export default NewWorkflowRequest;
